import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Copy,
    Check,
    CaretDown,
    CheckCircle,
    Clock,
    SpinnerGap,
    X,
    XCircle,
    Plus,
    Minus,
    ArrowCounterClockwise,
    PencilSimple,
    ThumbsUp,
    ThumbsDown,
} from '@phosphor-icons/react';
import {
    getDraftLinkedInPosts,
    getPublishedLinkedInPosts,
    getRejectedLinkedInPosts,
    getSavedLinkedInPosts,
    getLinkedInPostRejectReasons,
    rejectLinkedInPost,
    publishLinkedInPost,
    regenerateLinkedInPost,
    saveLinkedInPost,
    overrideLinkedInPost,
    unmarkLinkedInPost,
    saveLinkedInPostEdits,
    submitLinkedInPostRankingFeedback,
    getIdeaCardGateMode,
    setIdeaCardGateMode,
    getIdeaQueue,
    getRejectedIdeas,
    redraftLinkedInPost,
    sendBackLinkedInPost,
    decideIdeaCard,
    rejectCampaignTask,
} from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import useAnnouncer from '@/hooks/useAnnouncer';
import IdeaTabPanel from '@/components/post-studio/IdeaTabPanel';
import PostStudioCard, { PostStudioCardHeader } from '@/components/post-studio/PostStudioCard';
// The idea card's OWN reject vocabulary — one fetcher, one menu, shared with the Work Inbox
// card. See the block at the Ideas tab's reject prompt for why this replaced RejectReasonMenu.
import IdeaCardRejectMenu from '@/components/shared/IdeaCardRejectMenu';
import { AGENT_CONFIG, INTENT_TO_BUTTON, linkedInSignalPill, WORK_TYPES, SHARED_UI_LABELS, LINKEDIN_CONTENT_TYPES, LINKEDIN_CONTENT_TYPE_LABELS, IDEA_QUEUE_LABELS, REJECTED_SUBTABS, REJECTED_SUBTAB_LABELS, REJECTED_SUBTAB_ARIA_LABEL, IDEA_SUBTAB_ARIA_LABEL } from '@/config/task-display-config';
import { MODULE_REGISTRY, DESIGN_TOKENS } from '@/config/product-contract';
import { DESIGN_INTENTS, VERDICT_PILL, DS_BORDERS, DS_SPACING, DS_COLORS, DS_TYPOGRAPHY } from '@/config/ui-invariants';
import { toast } from 'sonner';
import LoopDetailsPanel from '@/components/post-studio/LoopDetailsPanel';
import GateAnnotationNotice from '@/components/post-studio/GateAnnotationNotice';
import UnresolvedNotes from '@/components/post-studio/UnresolvedNotes';

/**
 * Post Studio — Ideas / Pending / Saved / Posted / Rejected tabs
 * Authority: task-display-config.js -> AGENT_CONFIG.post_studio
 *
 * Route: /agents/linkedin-post
 *
 * ONE DOOR (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001, CEO ruling 2026-07-30).
 * The whole flow lives here, as tabs in the order the work actually moves: IDEA first, then
 * PENDING, then SAVED. The idea surface used to be a standalone screen at /ideas; a separate
 * idea screen is exactly how five ideas sat nine days unseen, so it was MOVED into the first
 * tab and the second door was removed — the /ideas route, its page component and its
 * module-registry entry are all gone. Posted and Rejected keep their places after Saved;
 * they are working surfaces and nothing asked for them to go.
 */

const PS = AGENT_CONFIG.post_studio || {};
const PS_TABS = PS.tabs || [
    { id: 'idea', label: 'Ideas' },
    { id: 'pending', label: 'Pending' },
    { id: 'saved', label: 'Saved' },
    { id: 'posted', label: 'Posted' },
    { id: 'rejected', label: 'Rejected' },
];
const PS_LABELS = PS.labels || {};

// WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 (CEO ruling 2026-07-17) — the confirmation the CEO
// hears names the tab he is being sent to, so it MUST be the label actually rendered on that
// tab. Read from PS_TABS rather than hardcoded: a tab rename then moves both together, and the
// announcement can never send him hunting for a tab that isn't on screen. (The ruling asked for
// "your Published tab" — there is no Published tab; it is "Posted".)
const PS_POSTED_TAB_LABEL = (PS_TABS.find(t => t.id === 'posted') || {}).label || 'Posted';
const withPostedTab = (template, fallback) =>
    (template || fallback).replace('{tab}', PS_POSTED_TAB_LABEL);
// WI-LINKEDIN-SAVE-BUTTON-LIVE-FAILURE-001 — stable id wiring the tabs (role="tab")
// to the content region (role="tabpanel") so the tablist is a valid ARIA pattern.
const PS_TABPANEL_ID = 'post-studio-tabpanel';

/**
 * IdeaCardGateToggle — the on-page pre-draft CEO gate control.
 * Authority: docs/contracts/IDEA_CARD_CONTRACT.md §2h (Card 4).
 *
 * Reads/writes the SAME config key the drafting pipeline obeys
 * (linkedin_post_rules.json::idea_card_gate_mode) via GET/PATCH
 * /api/linkedin-posts/idea-card-gate. The pipeline consults it through
 * idea_card_gate.advance_past_ceo_gate, so this toggle is never wired to a dead
 * config — the failure §2h names by name.
 *
 * State is driven from the API response (the PATCH read-after-write), never from
 * optimistic local state: if the write did not land, the switch must not claim it
 * did. Announces on success AND failure via the caller's aria-live announcer —
 * a toast alone is not reliably read to a screen reader (#950 standard).
 */
const GATE_MODE_MANDATORY = 'mandatory';
const GATE_MODE_SAMPLED = 'sampled';

function IdeaCardGateToggle({ announce }) {
    const [mode, setMode] = useState(null); // null = not loaded yet
    const [busy, setBusy] = useState(false);
    // Plain words from the server (26 Oct 2026): either "this is held only on the running system and the next update puts it back" or "an update has put it
    // back since you set it". Empty when there is nothing to say. The switch is written on the running server, not in the repository, so without this its
    // reversal was silent.
    const [notice, setNotice] = useState('');

    useEffect(() => {
        let cancelled = false;
        getIdeaCardGateMode()
            .then((res) => {
                if (!cancelled) {
                    setMode(res?.mode || GATE_MODE_SAMPLED);
                    setNotice(res?.notice || '');
                }
            })
            .catch(() => {
                if (cancelled) return;
                // Unknown, not "off" — never render a guessed state as fact.
                setMode(null);
                announce?.(PS_LABELS.gateToggleLoadError || 'Could not load the idea review setting.');
            });
        return () => { cancelled = true; };
    }, [announce]);

    const handleChange = useCallback(async (checked) => {
        if (busy) return;
        setBusy(true);
        const next = checked ? GATE_MODE_MANDATORY : GATE_MODE_SAMPLED;
        try {
            const res = await setIdeaCardGateMode(next);
            // Trust the server's read-back, not `next` — this is the proof the
            // flip landed in the config the pipeline actually reads.
            const landed = res?.mode || next;
            setMode(landed);
            setNotice(res?.notice || '');
            announce?.(
                landed === GATE_MODE_MANDATORY
                    ? (PS_LABELS.gateToggleOnAnnounce || 'On. Every idea now waits for your decision before drafting.')
                    : (PS_LABELS.gateToggleOffAnnounce || 'Off. Strong ideas go straight to drafting.'),
            );
        } catch (err) {
            const msg = err?.message || PS_LABELS.gateToggleSaveError || 'Could not change the setting — it is unchanged.';
            announce?.(msg);
            toast.error(msg);
            // Leave `mode` untouched — the switch keeps showing the last state the
            // server confirmed, so a failed flip never looks like a success.
        } finally {
            setBusy(false);
        }
    }, [busy, announce]);

    if (mode === null) return null;

    const label = PS_LABELS.gateToggleLabel || 'Review every idea before drafting';

    return (
        <div className="mb-6">
            <div className="flex items-center gap-3">
                <Switch
                    id="idea-card-gate-toggle"
                    checked={mode === GATE_MODE_MANDATORY}
                    onCheckedChange={handleChange}
                    disabled={busy}
                    aria-label={label}
                />
                <label
                    htmlFor="idea-card-gate-toggle"
                    className="text-base font-bold text-slate-800 cursor-pointer"
                >
                    {label}
                    <span className="block text-sm font-normal text-slate-500">
                        {PS_LABELS.gateToggleHint || ''}
                    </span>
                </label>
            </div>
            {notice && (
                <p
                    data-testid="idea-card-gate-notice"
                    role="status"
                    aria-live="polite"
                    className="mt-2 text-base font-bold text-slate-900"
                >
                    {notice}
                </p>
            )}
        </div>
    );
}

const PS_CALIBRATION = PS.calibrationOptions || [];
const PS_ACTIONS = PS.actions || [];

const REGEN_CONFIG = PS.regeneration || {};
const REGEN_MODIFIERS = REGEN_CONFIG.modifiers || [];
const REGEN_STATES = REGEN_CONFIG.states || { plus: 'plus', minus: 'minus', neutral: 'neutral' };

const MODULE = MODULE_REGISTRY.find(m => m.id === WORK_TYPES.LINKEDIN_POST) || {};

const POST_TYPE_LABELS = {
    thesis_confirmed: 'Thesis Confirmed',
    thesis_challenged: 'Thesis Challenged',
    standalone_insight: 'Standalone Insight',
    emerging_pattern: 'Emerging Pattern',
    contrarian_read: 'Contrarian Read',
};

/* --------- helper: find action by id --------- */
const findAction = (id) => PS_ACTIONS.find(a => a.id === id) || {};

/* --------- helper: days elapsed since an ISO timestamp --------- */
function daysSince(isoString) {
    if (!isoString) return null;
    const then = new Date(isoString);
    if (isNaN(then.getTime())) return null;
    const diffMs = Date.now() - then.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/* --------- CopyButton --------- */

function CopyButton({ text, label, announce }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            const msg = (PS_LABELS.copySuccess || '{label} copied.').replace('{label}', label);
            toast.success(msg);
            if (announce) announce(msg);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            const msg = PS_LABELS.copyError || 'Could not copy — try selecting manually.';
            toast.error(msg);
            if (announce) announce(msg);
        }
    }, [text, label, announce]);

    return (
        <button
            onClick={handleCopy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${INTENT_TO_BUTTON.neutral.bg} text-base font-semibold ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} transition-colors`}
            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
        >
            {copied ? (
                <><Check weight="bold" className="w-4 h-4 text-green-600" /> Copied</>
            ) : (
                <><Copy weight="bold" className="w-4 h-4" /> {label}</>
            )}
        </button>
    );
}

/* --------- SignalPill --------- */

function SignalPill({ signal }) {
    let mapping;
    if (typeof signal === 'string') {
        mapping = linkedInSignalPill[signal];
    } else if (typeof signal === 'number') {
        mapping = signal >= 0.8
            ? linkedInSignalPill.strong_signal
            : signal >= 0.5
                ? linkedInSignalPill.signal
                : linkedInSignalPill.noise;
    }
    if (!mapping) mapping = linkedInSignalPill.signal;

    const pill = VERDICT_PILL[mapping.verdict] || VERDICT_PILL.moderate;
    const shape = VERDICT_PILL.pill;

    return (
        <span
            className="inline-flex items-center gap-1.5"
            style={{
                background: pill.background,
                color: pill.text,
                border: `${shape.borderWidth} solid ${pill.border}`,
                borderRadius: shape.borderRadius,
                fontSize: shape.fontSize,
                fontWeight: shape.fontWeight,
                paddingTop: shape.paddingTop,
                paddingBottom: shape.paddingBottom,
                paddingLeft: shape.paddingLeft,
                paddingRight: shape.paddingRight,
            }}
            aria-label={`Signal: ${mapping.label}`}
        >
            <span
                style={{
                    width: pill.dotSize,
                    height: pill.dotSize,
                    borderRadius: '50%',
                    background: pill.dot,
                    display: 'inline-block',
                }}
            />
            {mapping.label}
        </span>
    );
}

/* --------- RejectReasonMenu ---------
 * WI-LINKEDIN-REJECT-REASONS-AND-LEARNING-LOOP-001: one grouped, one-tap reject
 * menu shared by PendingPostCard and SavedPostCard. Picking any reason fires
 * the reject immediately with the group's derived attribution — no separate
 * attribution step. `taxonomy` is { groups: [...], other: {...}|null } from
 * GET /api/linkedin-posts/reject-reasons; empty groups render nothing (never
 * crashes if the fetch failed). */
function RejectReasonMenu({ taxonomy, onSelect }) {
    const groups = taxonomy?.groups || [];
    const other = taxonomy?.other || null;

    return (
        // DECISION: no role="menu"/"menuitem" here — these remain plain, native
        // <button> elements (already keyboard/SR operable via their implicit
        // button role) inside a labeled region. Adding ARIA menu semantics
        // without the full menu keyboard protocol (arrow-key roving focus,
        // Home/End, Escape-to-close) is a known anti-pattern that overrides the
        // native button role and can confuse AT users expecting menu navigation
        // that isn't implemented. The discoverability fix lives on the toggle
        // button (aria-haspopup + aria-expanded below), mirroring the disclosure
        // pattern already used by CollapsibleCardHeader (#950) — one pattern,
        // not two.
        <div
            aria-label={PS_LABELS.rejectReasonMenuLabel || 'Reject reason'}
            className="absolute bottom-full left-0 mb-2 w-72 bg-white rounded-xl border border-slate-200 shadow-lg py-2 z-20 max-h-96 overflow-y-auto"
        >
            {groups.map(group => (
                <div key={group.attribution}>
                    <div
                        className="px-4 pt-2 pb-1 text-sm font-bold uppercase tracking-wide text-slate-500"
                    >
                        {group.group_label}
                    </div>
                    {(group.reasons || []).map(reason => (
                        <button
                            key={reason.id}
                            onClick={() => onSelect(reason.id, group.attribution)}
                            className="w-full text-left px-4 py-2.5 text-base text-slate-800 hover:bg-slate-50 font-semibold"
                            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {reason.label}
                        </button>
                    ))}
                </div>
            ))}
            {other && (
                <>
                    <div className="my-1 border-t border-slate-100" />
                    <button
                        onClick={() => onSelect(other.id, null)}
                        className="w-full text-left px-4 py-2.5 text-base text-slate-800 hover:bg-slate-50 font-semibold"
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                    >
                        {other.label}
                    </button>
                </>
            )}
        </div>
    );
}

// WHAT EACH TAB'S COUNT IS COUNTING, spoken aloud.
// Every tab used to announce "N posts", so Ideas read out as "Ideas, 5 posts" — five ideas,
// not five posts. The CEO hears this surface rather than seeing it, so the noun is the
// difference between an accurate reading and a wrong one. Falls back to 'post' so a tab added
// without a noun still reads sensibly rather than crashing.
// WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001 (CEO ruling 2026-07-30).
const tabCountNoun = (tab, count) => {
    const noun = tab?.noun || 'post';
    return count === 1 ? noun : `${noun}s`;
};

// A TAB'S NUMBER COUNTS WHAT NEEDS HIM, AND LEAVES OUT WHAT DOES NOT (CEO, 14 Oct 2026, 18:06).
// The Ideas number was `ideas.length`, which is the first group only ("Waiting on you"), so a card
// held back by one of our checks sat behind its chip for four days with no number anywhere. The
// held-back group is counted because nobody has been able to decide it yet; the queued group is
// not, because it is already decided and waits on the machine. If the backend sends no groups
// (an older cached payload) the count is the old one, never zero.
const IDEA_GROUPS_THAT_NEED_HIM = ['awaiting_you', 'held_back'];
const countIdeasThatNeedHim = (ideas, summary) => {
    const groups = summary?.groups;
    if (!Array.isArray(groups) || groups.length === 0) return ideas.length;
    return groups
        .filter(g => IDEA_GROUPS_THAT_NEED_HIM.includes(g.id))
        .reduce((n, g) => n + (g.count || 0), 0);
};

/* --------- WithheldDrafts --------- */
//
// WHAT IS BEING HELD BACK, AND WHY (WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001).
//
// The archive flag reversibly removes a finished draft from the Pending tab. That is a real
// feature and nothing here undoes it. But the effect was total silence: one draft — 1,502
// characters of finished text, written 2026-07-10, archived 2026-07-13 — had never appeared on
// any screen, while the tab read 0. An honest 0 about what is PENDING is still a silence about
// what is WITHHELD.
//
// So it is NAMED rather than promoted: the CEO can see the draft exists, read it, and see the
// reason it is not in the list. Its status is not changed and it is not counted as pending.
//
// THE CARD TREATMENT, AND WHY IT CHANGED (WI-POST-STUDIO-VERIFY-LIVE-AND-REVERT-PENDING-BY-DIFF-001,
// CEO ruling 2026-07-30 recorded at P-124).
//
// This block shipped as its own treatment — rounded-xl, a flat border on all four sides, p-4 —
// while Pending, Saved, Posted and Rejected are all lists of ONE card: white surface,
// rounded-2xl, a 4px navy left accent, 1px borders on the other three sides. With zero pending
// drafts this notice is the ONLY substantive thing on the tab, so its treatment WAS the tab's
// treatment, and the tab read as a different screen from its four siblings.
//
// It now renders inside PostStudioCard — the same shell those four use, extracted rather than
// copied a sixth time. Nothing here is redesigned: the heading, the reason and the facts are
// the same sentences in the same order, from the same labels.
//
// The body no longer dumps inline. 1,502 characters of finished text rendered in full is the
// other half of why this matched nothing else — every sibling tab collapses a long post body
// behind a control. It uses the tab's OWN declared cardCollapsible config, not a second rule.
function WithheldDrafts({ items = [] }) {
    const PS_LOCAL = AGENT_CONFIG.post_studio || {};
    const COLLAPSE = PS_LOCAL.cardCollapsible || { defaultCollapsed: true, headerMinHeight: 44, expandedShadow: true };
    const [openId, setOpenId] = useState(COLLAPSE.defaultCollapsed === false ? 'ALL' : null);

    if (!items.length) return null;
    return (
        <PostStudioCard data-testid="withheld-drafts" className="p-6">
            <p className="text-base font-bold" style={{ color: DS_COLORS.text.primary }}>
                {IDEA_QUEUE_LABELS.withheldHeading(items.length)}
            </p>
            <p className="text-base mt-1" style={{ color: DS_COLORS.text.secondary }}>
                {IDEA_QUEUE_LABELS.withheldWhy}
            </p>
            <ul className="mt-3 space-y-3">
                {items.map(p => {
                    const id = p._id || p.post_id;
                    const text = p.post_text || p.post_text_original || '';
                    const isOpen = openId === 'ALL' || openId === id;
                    return (
                        <li
                            key={id}
                            data-withheld-id={id}
                            className="border-t pt-3"
                            style={{ borderColor: DS_COLORS.border.default }}
                        >
                            <p className="text-base font-semibold" style={{ color: DS_COLORS.text.primary }}>
                                {p.source_article_title || IDEA_QUEUE_LABELS.statusMissing}
                            </p>
                            <p className="text-base" style={{ color: DS_COLORS.text.secondary }}>
                                {IDEA_QUEUE_LABELS.withheldOn} {(p.archived_at || '').slice(0, 10)}
                                {text ? ` · ${IDEA_QUEUE_LABELS.withheldChars(text.length)}` : ''}
                            </p>
                            {p.archive_reason && (
                                <p data-testid={`withheld-reason-${id}`} className="text-base" style={{ color: DS_COLORS.text.secondary }}>
                                    {IDEA_QUEUE_LABELS.withheldReasonLead} {p.archive_reason}
                                </p>
                            )}
                            {text && (
                                <>
                                    <button
                                        type="button"
                                        data-testid={`withheld-toggle-${id}`}
                                        aria-expanded={isOpen}
                                        onClick={() => setOpenId(isOpen ? null : id)}
                                        className="text-base font-semibold mt-2 underline"
                                        style={{
                                            color: DS_COLORS.text.brand,
                                            minHeight: COLLAPSE.headerMinHeight,
                                        }}
                                    >
                                        {isOpen
                                            ? IDEA_QUEUE_LABELS.withheldHideFull
                                            : IDEA_QUEUE_LABELS.withheldReadFull}
                                    </button>
                                    {isOpen && (
                                        <p
                                            data-testid={`withheld-body-${id}`}
                                            className="text-base mt-2 whitespace-pre-wrap leading-relaxed"
                                            style={{ color: DS_COLORS.text.primary }}
                                        >
                                            {text}
                                        </p>
                                    )}
                                </>
                            )}
                        </li>
                    );
                })}
            </ul>
        </PostStudioCard>
    );
}

/* --------- TabChips --------- */
//
// THE 16px FLOOR ON THIS TABLIST, AND WHY IT WAS STILL BROKEN
// (WI-ONE-POST-REACHES-THE-CEO-001 Task 4, CEO rulings D-2026-07-31-16 / CLAUDE.md rule 13.)
//
// Measured on the DEPLOYED page on 2026-08-02 with getComputedStyle: every tab label
// rendered at 14px and every count badge at 12px, on the surface the CEO navigates by.
// The labels and badges below now carry the 16px step. The `h-5`/`w-5` fixed box went
// with the badge's type size — a 20px box cannot hold 16px text, and pinning the box
// while raising the text is how a "fix" turns into a clipped digit.

/**
 * @param {Array}  tabs       Which tabs to render. Defaults to the page's five.
 * @param {string} ariaLabel  The tablist's name.
 * @param {string} controls   id of the panel this tablist drives, or undefined.
 *
 * GENERALISED, NOT COPIED (WI-ONE-POST-REACHES-THE-CEO-001 Leg 3). The Rejected tab's
 * two sub-tabs were first written here as a second chip row, and that hand-rolled row
 * silently dropped the ruling this component exists to carry: UNKNOWN IS NOT ZERO. It
 * rendered "Ideas (0)" for a list that had FAILED TO LOAD — the precise lie the counter
 * work of 2026-07-30 was done to end. Three props and the sub-tabs inherit the ARIA
 * tablist, the 44px target, the count badge and the unknown-count honesty for free.
 *
 * @param {string} variant  'solid' (default, unchanged) — the top-level 5-tab bar's
 *   selected state, a solid navy fill (INTENT_TO_BUTTON.primary). 'tint' — the Ideas
 *   tab's three sub-tabs and Rejected's two sub-tabs (WI-NIGHT-CLOSE-UI-RESTORE-AND-
 *   AMNESTY-STAGGER-001 Leg B3): selected state is the brand navy TINT
 *   (DS_COLORS.surface.warm, #F0F0F8 — DESIGN_LANGUAGE_SKILL.md's "Brand navy tint",
 *   the SAME token ACTION_BUTTONS.knobStates.active already uses for a selected knob),
 *   a lighter weight than the solid fill so a sub-tab reads as one level down from the
 *   page's own tabs rather than repeating their exact treatment.
 */
function TabChips({ activeTab, onTabChange, tabCounts, tabs = PS_TABS, ariaLabel = PS_LABELS.tablistLabel, controls = PS_TABPANEL_ID, variant = 'solid' }) {
    return (
        // WI-LINKEDIN-SAVE-BUTTON-LIVE-FAILURE-001 — real ARIA tablist so a
        // screen reader announces each view and which one is selected. Previously
        // these were anonymous buttons: the CEO (screen-reader user) could not
        // perceive the active tab or that it changed.
        <div className="flex items-center gap-2 mb-6" role="tablist" aria-label={ariaLabel}>
            {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                // UNKNOWN IS NOT ZERO (CEO ruling 2026-07-30). `tabCounts` holds null for a
                // tab whose list could not be loaded, and a number when it could. The old
                // `tabCounts[tab.id] || 0` collapsed both into 0, so a dead endpoint and a
                // genuinely empty tab looked identical — the exact defect that let dead
                // counters sit unnoticed for months. A real 0 still shows no badge; an
                // unknown says so, in words, in the badge and to a screen reader.
                const rawCount = tabCounts[tab.id];
                const isUnknown = rawCount == null;
                const count = isUnknown ? 0 : rawCount;
                const isTint = variant === 'tint';
                // TINT VARIANT active state — inline style ONLY on this branch, so it never
                // shares the `background`/`color`/`border-color` properties with a className
                // on the same element (DESIGN_LANGUAGE_SKILL.md §7 rule 2, split-authority).
                // The 'solid' branch below is untouched — same Tailwind classes as before.
                const tintActiveStyle =
                    isTint && isActive
                        ? {
                              background: DS_COLORS.surface.warm,
                              color: DS_COLORS.text.brand,
                              borderColor: DS_COLORS.border.brand,
                          }
                        : {};
                // Count badge: on the solid variant's active pill (navy fill) the badge
                // inverts to white-on-navy so it stands out. On the tint variant's active
                // pill (light background) that inversion would read as a near-invisible
                // white-on-tint badge, so it keeps the SAME navy-on-white treatment the
                // inactive pill already uses instead.
                const badgeStyle = {
                    background: isActive && !isTint ? DS_COLORS.surface.white : DS_COLORS.text.brand,
                    color: isActive && !isTint ? DS_COLORS.text.brand : DS_COLORS.surface.white,
                };
                return (
                    <button
                        key={tab.id}
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={controls}
                        aria-label={
                            isUnknown
                                ? `${tab.label}, ${SHARED_UI_LABELS.countUnknownSpoken}`
                                : `${tab.label}, ${count} ${tabCountNoun(tab, count)}`
                        }
                        onClick={() => onTabChange(tab.id)}
                        className={`inline-flex items-center gap-2 px-5 rounded-full text-base font-semibold transition-colors border ${
                            isActive
                                ? (isTint
                                    ? ''
                                    : `${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border}`)
                                : `${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover}`
                        }`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px', ...tintActiveStyle }}
                    >
                        {tab.label}
                        {isUnknown && (
                            <span
                                aria-hidden="true"
                                data-testid={`tab-count-unknown-${tab.id}`}
                                className="inline-flex items-center justify-center px-2 rounded-full text-base font-bold"
                                style={badgeStyle}
                            >
                                {SHARED_UI_LABELS.countUnknown}
                            </span>
                        )}
                        {!isUnknown && count > 0 && (
                            <span
                                aria-hidden="true"
                                className="inline-flex items-center justify-center px-2 rounded-full text-base font-bold"
                                style={badgeStyle}
                            >
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

/* --------- ModifierDial --------- */

function ModifierDial({ label, state, disabled, onChange }) {
    const isPlus = state === REGEN_STATES.plus;
    const isMinus = state === REGEN_STATES.minus;

    return (
        <div className={`flex items-center justify-between py-2 ${disabled ? 'opacity-40' : ''}`}>
            <span className="text-base font-bold text-slate-800">{label}</span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onChange(isMinus ? REGEN_STATES.neutral : REGEN_STATES.minus)}
                    disabled={disabled}
                    className={[
                        'w-[44px] h-[44px] flex items-center justify-center rounded-lg border-2 transition-colors font-bold text-lg',
                        isMinus
                            ? `${DESIGN_INTENTS.negative.bg} ${DESIGN_INTENTS.negative.text} ${DESIGN_INTENTS.negative.border}`
                            : 'bg-white text-slate-400 border-slate-200 hover:border-brand-900 hover:text-brand-900',
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                    aria-label={`Decrease ${label}`}
                    aria-pressed={isMinus}
                >
                    <Minus weight="bold" className="w-5 h-5" />
                </button>
                <div className={`w-3 h-3 rounded-full mx-1 ${
                    !isPlus && !isMinus ? 'bg-slate-400' : 'bg-slate-200'
                }`} />
                <button
                    onClick={() => onChange(isPlus ? REGEN_STATES.neutral : REGEN_STATES.plus)}
                    disabled={disabled}
                    className={[
                        'w-[44px] h-[44px] flex items-center justify-center rounded-lg border-2 transition-colors font-bold text-lg',
                        isPlus
                            ? `${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.text} ${DESIGN_INTENTS.positive.border}`
                            : 'bg-white text-slate-400 border-slate-200 hover:border-brand-900 hover:text-brand-900',
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                    aria-label={`Increase ${label}`}
                    aria-pressed={isPlus}
                >
                    <Plus weight="bold" className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
}

/* --------- CollapsibleCardHeader (TC-07) --------- */
/* Two-row quick-scan layout — see DESIGN_LANGUAGE_SKILL.md Section 10 */

function CollapsibleCardHeader({ post, isExpanded, onToggle }) {
    const postType = post.post_type || 'post';
    // DECISION: content_type absent on legacy text records — treat as 'text' per field contract.
    const contentType = post.content_type || LINKEDIN_CONTENT_TYPES.TEXT;
    const isVideo = contentType === LINKEDIN_CONTENT_TYPES.VIDEO;
    const contentTypeLabel = LINKEDIN_CONTENT_TYPE_LABELS[contentType] || LINKEDIN_CONTENT_TYPE_LABELS[LINKEDIN_CONTENT_TYPES.TEXT];
    const sourceTitle = post.source_article_title || '';
    const agentRec = post.article_agent_analysis?.recommendation;
    const agentRecommendsSave = agentRec === 'save';
    // WI-LINKEDIN-POST-RANKER-V1 — Ranker score badge + one-line rationale.
    // ranker_score is null when the Ranker call failed; in that case we render
    // neither the badge nor the rationale row.
    const rankerScore = post.ranker_score;
    const rankerRationale = post.ranker_rationale;
    const hasRankerScore = typeof rankerScore === 'number' && Number.isFinite(rankerScore);
    const hasRationale = hasRankerScore && rankerRationale && rankerRationale !== 'Ranker unavailable';

    const rawLabel = sourceTitle || (POST_TYPE_LABELS[postType] || postType.replace(/_/g, ' '));
    // TC-07: Convert ALL CAPS headlines to title case per DESIGN_LANGUAGE_SKILL.md Section 10
    const dominantLabel = rawLabel.length > 1
        ? rawLabel.toLowerCase().replace(/(^|\s)(\S)/g, (_, sp, c) => sp + c.toUpperCase())
        : rawLabel;

    // THE POST'S OWN FACTS, AS PILLS
    // (WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001).
    //
    // The markup that used to live here now lives in PostStudioCardHeader, shared with the
    // Ideas tab and the withheld-draft notice. This function keeps ONLY the part that is
    // genuinely a post's own — which fields become which pills, in which order. Same pills,
    // same order, same intents as before; nothing about what a post shows changes.
    const pills = [
        {
            text: contentTypeLabel,
            intent: isVideo ? 'warning' : 'neutral',
            ariaLabel: PS_LABELS.contentTypeAria(contentTypeLabel),
        },
        {
            text: POST_TYPE_LABELS[postType] || postType.replace(/_/g, ' '),
            intent: 'info',
        },
        ...(hasRankerScore ? [{
            text: PS_LABELS.scorePill(Math.round(rankerScore)),
            intent: 'primary',
            bold: true,
            title: rankerRationale || PS_LABELS.scorePillTooltip,
        }] : []),
        {
            text: agentRecommendsSave ? PS_LABELS.agentRecommendsSave : PS_LABELS.agentRecommendsReject,
            intent: agentRecommendsSave ? 'positive' : 'warning',
        },
    ];

    return (
        <PostStudioCardHeader
            title={dominantLabel}
            pills={pills}
            summaryLine={hasRationale ? rankerRationale : undefined}
            isExpanded={isExpanded}
            onToggle={onToggle}
        />
    );
}

/* --------- PendingPostCard --------- */

function PendingPostCard({ post, onPosted, onRejected, onSaved, rankPosition, totalInSession, rejectTaxonomy, announce }) {
    const CARD_COLLAPSE = PS.cardCollapsible || { defaultCollapsed: true, headerMinHeight: 44, expandedShadow: true };
    const [isExpanded, setIsExpanded] = useState(!CARD_COLLAPSE.defaultCollapsed);
    const [calibration, setCalibration] = useState('agent_got_it_right');
    const [showRejectDropdown, setShowRejectDropdown] = useState(false);
    const [showSendBackDropdown, setShowSendBackDropdown] = useState(false);
    const [busy, setBusy] = useState(false);
    const [busyAction, setBusyAction] = useState(null);
    // WI-LINKEDIN-POST-FRONTEND-V1 — per-card Ranker thumbs feedback. Local-only
    // state; on click we post optimistically and lock the choice in. Independent
    // of the post-decision calibration above (which feeds a separate signal).
    const [rankingFeedbackChoice, setRankingFeedbackChoice] = useState(null);
    const [rankingFeedbackPending, setRankingFeedbackPending] = useState(false);

    // Regeneration state
    const [modifiers, setModifiers] = useState(() => {
        const initial = {};
        REGEN_MODIFIERS.forEach(m => { initial[m.key] = REGEN_STATES.neutral; });
        return initial;
    });
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [localPostText, setLocalPostText] = useState(null);

    const postText = localPostText ?? (post.post_text || '');
    const firstComment = post.optimizer_output?.first_comment_text || post.first_comment_text;
    const postType = post.post_type || 'post';
    const isDraft = (post.status || '').toLowerCase() === 'draft';

    const postId = post._id || post.post_id;

    const hasActiveModifiers = useMemo(
        () => Object.values(modifiers).some(v => v !== REGEN_STATES.neutral),
        [modifiers]
    );

    const handleModifierChange = useCallback((key, newState) => {
        setModifiers(prev => ({ ...prev, [key]: newState }));
    }, []);

    const handleRegenerate = useCallback(async () => {
        if (!hasActiveModifiers || isRegenerating) return;
        setIsRegenerating(true);
        try {
            const result = await regenerateLinkedInPost(postId, modifiers);
            setLocalPostText(result.post_text);
            setModifiers(prev => {
                const reset = {};
                Object.keys(prev).forEach(k => { reset[k] = REGEN_STATES.neutral; });
                return reset;
            });
            const regenMsg = REGEN_CONFIG.successMessage || 'Draft updated';
            toast.success(regenMsg);
            announce(regenMsg);
        } catch (err) {
            // Spec TC-08: branch on err.status. err.message carries the backend `detail`.
            let regenMsg;
            if (err?.status === 422) {
                regenMsg = REGEN_CONFIG.gateFailedMessage || "Draft didn't pass quality review \u2014 try different settings";
            } else if (err?.status === 400) {
                regenMsg = err.message || REGEN_CONFIG.errorMessage || 'Something went wrong \u2014 please try again';
            } else {
                regenMsg = REGEN_CONFIG.errorMessage || 'Something went wrong \u2014 please try again';
            }
            toast.error(regenMsg);
            announce(regenMsg);
        } finally {
            setIsRegenerating(false);
        }
    }, [hasActiveModifiers, isRegenerating, postId, modifiers, announce]);

    const handlePost = useCallback(async () => {
        setBusy(true);
        setBusyAction('post');
        try {
            await publishLinkedInPost(postId, calibration);
            // WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 \u2014 copy corrected ("Posted \u2014 it's live."
            // was never true; nothing here posts to LinkedIn). Deliberately NO announce() on
            // this success path: onPosted -> the page's handlePosted already announces the
            // transition and moves focus. Announcing here too would speak twice.
            toast.success(withPostedTab(PS_LABELS.postSuccess, 'Marked as published and moved to your {tab} tab.'));
            onPosted(postId);
        } catch (err) {
            // Honest message: prefer the backend's real reason (e.g. the 409
            // publishing-paused detail) over a generic "try again".
            const msg = err?.message || PS_LABELS.postError || 'Could not post \u2014 please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, calibration, onPosted, announce]);

    const handleReject = useCallback(async (reasonId, attribution) => {
        setShowRejectDropdown(false);
        setBusy(true);
        setBusyAction('reject');
        try {
            await rejectLinkedInPost(postId, reasonId, calibration, attribution);
            toast.success(PS_LABELS.rejectSuccess || 'Post rejected.');
            onRejected(postId);
        } catch (err) {
            const msg = err?.message || PS_LABELS.rejectError || 'Could not reject \u2014 please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, calibration, onRejected, announce]);

    // R24: send the SAME approved idea back for a versioned rewrite, with a writing reason. Not a rejection of the idea.
    // The server rewrites before it answers; the STATUS decides, never the HTTP code.
    const handleSendBack = useCallback(async (reasonId) => {
        setShowSendBackDropdown(false);
        setBusy(true);
        setBusyAction('sendback');
        try {
            const result = await sendBackLinkedInPost(postId, reasonId);
            if (result?.status === 'redrafted') {
                if (result.draft?.post_text) setLocalPostText(result.draft.post_text);
                const msg = 'Sent back. The draft below is the new version.';
                toast.success(msg);
                announce(msg);
                return;
            }
            const msg = result?.message || result?.reason || 'The rewrite did not produce a new draft.';
            toast.error(msg);
            announce(msg);
        } catch (err) {
            const msg = err?.message || 'Could not send this back. Please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, announce]);

    const handleSave = useCallback(async () => {
        setBusy(true);
        setBusyAction('save');
        try {
            await saveLinkedInPost(postId);
            toast.success(PS_LABELS.saveSuccess || 'Draft saved');
            onSaved(post);
        } catch (err) {
            const msg = err?.message || PS_LABELS.saveError || 'Could not save draft';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, onSaved, post, announce]);

    // Ranker thumbs feedback handler — independent of the post decision.
    const handleRankingFeedback = useCallback(async (direction) => {
        if (rankingFeedbackChoice || rankingFeedbackPending) return;
        if (typeof post.ranker_score !== 'number' || !Number.isFinite(post.ranker_score)) return;
        const previous = rankingFeedbackChoice;
        setRankingFeedbackChoice(direction);
        setRankingFeedbackPending(true);
        try {
            await submitLinkedInPostRankingFeedback(
                postId,
                direction,
                rankPosition || 1,
                totalInSession || 1,
            );
            const msg = direction === 'up'
                ? (PS_LABELS.rankerFeedbackUpSuccess || 'Thanks')
                : (PS_LABELS.rankerFeedbackDownSuccess || 'Thanks');
            toast.success(msg);
            announce(msg);
        } catch (err) {
            // Revert on failure so the CEO can retry.
            setRankingFeedbackChoice(previous);
            const msg = err?.message || PS_LABELS.rankerFeedbackError || 'Could not record that — please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setRankingFeedbackPending(false);
        }
    }, [postId, rankPosition, totalInSession, rankingFeedbackChoice, rankingFeedbackPending, post.ranker_score, announce]);

    const rejectAction = findAction('reject');
    const saveDraftAction = findAction('save_draft');
    const postAction = findAction('post');
    const hasRankerScore = typeof post.ranker_score === 'number' && Number.isFinite(post.ranker_score);

    return (
        // THE FIFTH COPY IS GONE (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 2).
        // This shell was hand-written here, byte-identically, four times. PostStudioCard's
        // `accent` and `elevated` props reproduce the exact conditional that stood here —
        // isExpanded took the border to DS_BORDERS.color and otherwise to DS_COLORS.border.brand,
        // and the shadow was raised only while expanded — so nothing the CEO can see changes.
        <PostStudioCard
            accent={isExpanded ? 'muted' : 'brand'}
            elevated={Boolean(isExpanded && CARD_COLLAPSE.expandedShadow)}
        >
            <CollapsibleCardHeader
                post={post}
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(prev => !prev)}
            />

            {isExpanded && (
                    <div className="p-6">
                        {/* Per-card Ranker thumbs — WI-LINKEDIN-POST-FRONTEND-V1.
                            Independent of the post-decision calibration below; this
                            signal captures CEO judgment on the Ranker's ordering,
                            not on the draft itself. Hidden when ranker_score is null. */}
                        {hasRankerScore && (
                            <div className="mb-4 flex items-center gap-3 flex-wrap">
                                <span className="text-sm font-bold text-slate-700">
                                    {PS_LABELS.rankerFeedbackPrompt || 'Was this rank right?'}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleRankingFeedback('up')}
                                        disabled={Boolean(rankingFeedbackChoice) || rankingFeedbackPending}
                                        aria-label={PS_LABELS.rankerFeedbackThumbsUpAria || 'Thumbs up'}
                                        aria-pressed={rankingFeedbackChoice === 'up'}
                                        className={[
                                            'inline-flex items-center justify-center rounded-lg border-2 transition-colors',
                                            rankingFeedbackChoice === 'up'
                                                ? `${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.text} ${DESIGN_INTENTS.positive.border}`
                                                : 'bg-white text-slate-500 border-slate-200 hover:border-brand-900 hover:text-brand-900',
                                            rankingFeedbackChoice && rankingFeedbackChoice !== 'up' ? 'opacity-40' : '',
                                            (Boolean(rankingFeedbackChoice) || rankingFeedbackPending) ? 'cursor-not-allowed' : 'cursor-pointer',
                                        ].join(' ')}
                                        style={{ width: DS_SPACING.minTouchTarget + 'px', height: DS_SPACING.minTouchTarget + 'px' }}
                                    >
                                        <ThumbsUp weight={rankingFeedbackChoice === 'up' ? 'fill' : 'bold'} className="w-5 h-5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleRankingFeedback('down')}
                                        disabled={Boolean(rankingFeedbackChoice) || rankingFeedbackPending}
                                        aria-label={PS_LABELS.rankerFeedbackThumbsDownAria || 'Thumbs down'}
                                        aria-pressed={rankingFeedbackChoice === 'down'}
                                        className={[
                                            'inline-flex items-center justify-center rounded-lg border-2 transition-colors',
                                            rankingFeedbackChoice === 'down'
                                                ? `${DESIGN_INTENTS.negative.bg} ${DESIGN_INTENTS.negative.text} ${DESIGN_INTENTS.negative.border}`
                                                : 'bg-white text-slate-500 border-slate-200 hover:border-brand-900 hover:text-brand-900',
                                            rankingFeedbackChoice && rankingFeedbackChoice !== 'down' ? 'opacity-40' : '',
                                            (Boolean(rankingFeedbackChoice) || rankingFeedbackPending) ? 'cursor-not-allowed' : 'cursor-pointer',
                                        ].join(' ')}
                                        style={{ width: DS_SPACING.minTouchTarget + 'px', height: DS_SPACING.minTouchTarget + 'px' }}
                                    >
                                        <ThumbsDown weight={rankingFeedbackChoice === 'down' ? 'fill' : 'bold'} className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}

                        <LoopDetailsPanel post={post} />

                        {/* WI-ONE-POST-REACHES-THE-CEO-001 Leg 4 — the check that fired, ABOVE
                            the draft it fired on, because it quotes a sentence from it and the
                            quote is meaningless read after the text it came from. Renders
                            nothing when no check fired. */}
                        <GateAnnotationNotice annotations={post.gate_annotations} />
                        <UnresolvedNotes notes={post.unresolved_notes} />

                        {/* Post text block */}
                        <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 mb-4 relative">
                            <p className="text-base text-slate-800 whitespace-pre-wrap leading-relaxed">
                                {postText}
                            </p>
                            <div className="absolute top-3 right-3">
                                <CopyButton text={postText} label="Copy" announce={announce} />
                            </div>
                        </div>

                        {/* First comment block */}
                        {firstComment && (
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-semibold text-slate-500">First Comment</span>
                                    <CopyButton text={firstComment} label={SHARED_UI_LABELS.copyComment} announce={announce} />
                                </div>
                                <div className="bg-amber-50 rounded-lg border border-amber-100 p-3">
                                    <p className="text-sm text-slate-700">{firstComment}</p>
                                </div>
                            </div>
                        )}

                        {/* Calibration feedback pills */}
                        <div className="mb-4">
                            <p className="text-sm font-bold text-slate-700 mb-2">How did the agent do?</p>
                            <div className="flex flex-wrap gap-2">
                                {PS_CALIBRATION.map(opt => {
                                    const isSelected = calibration === opt.id;
                                    return (
                                        <button
                                            key={opt.id}
                                            onClick={() => {
                                                setCalibration(opt.id);
                                                announce((PS_LABELS.calibrationSelected || 'Marked calibration: {label}.').replace('{label}', opt.label));
                                            }}
                                            aria-pressed={isSelected}
                                            className={`px-3 py-1.5 border text-sm font-semibold transition-colors ${
                                                isSelected
                                                    ? ''
                                                    : `${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover}`
                                            }`}
                                            style={{
                                                minHeight: DS_SPACING.minTouchTarget + 'px',
                                                borderRadius: DS_BORDERS.radiusPill,
                                                ...(isSelected ? {
                                                    borderColor: DS_COLORS.border.brand,
                                                    color: DS_COLORS.text.brand,
                                                    background: DS_COLORS.surface.warm,
                                                } : {}),
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Knobs section (only for draft posts) */}
                        {isDraft && REGEN_MODIFIERS.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
                                <span className="text-base font-bold text-slate-800 block mb-3">
                                    Adjust Draft
                                </span>
                                <div className="space-y-1">
                                    {REGEN_MODIFIERS.map(mod => {
                                        const isDisabled = (mod.disabled_for || []).includes(postType);
                                        return (
                                            <ModifierDial
                                                key={mod.key}
                                                label={mod.label}
                                                state={modifiers[mod.key] || REGEN_STATES.neutral}
                                                disabled={isDisabled || isRegenerating}
                                                onChange={(newState) => handleModifierChange(mod.key, newState)}
                                            />
                                        );
                                    })}
                                </div>
                                <div className="mt-4">
                                    <button
                                        disabled={!hasActiveModifiers || isRegenerating || busy}
                                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                        onClick={handleRegenerate}
                                    >
                                        {isRegenerating ? (
                                            <>
                                                <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                                                {REGEN_CONFIG.loadingLabel}
                                            </>
                                        ) : (
                                            <>
                                                <ArrowCounterClockwise weight="bold" className="w-4 h-4" />
                                                {REGEN_CONFIG.buttonLabel}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

            )}

            {/* THE ACTIONS LIVE OUTSIDE THE COLLAPSING REGION
                (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 3b).

                This bar used to sit INSIDE the `isExpanded` branch, so a collapsed card had no
                working buttons at all — the CEO had to open a card before he could act on it,
                and on a list of collapsed cards there was nothing to press. The decision does
                not depend on the body being visible, so neither should the controls. Only the
                BODY collapses now; the actions are always mounted and always operable. */}
                    {/* Action bar: Reject (left) | Save (middle) | Post (right) */}
                    <div className="sticky bottom-0 bg-white border-t border-slate-200 px-8 py-3.5 flex items-center gap-3 z-10" style={{ boxShadow: DS_COLORS.shadow.actionBar }}>
                        <div className="relative">
                            <button
                                onClick={() => setShowRejectDropdown(!showRejectDropdown)}
                                disabled={busy}
                                aria-haspopup="true"
                                aria-expanded={showRejectDropdown}
                                className={`inline-flex items-center gap-2 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-50`}
                                style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                            >
                                {busyAction === 'reject' ? (
                                    <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                                ) : (
                                    <X weight="bold" className="w-4 h-4" />
                                )}
                                {rejectAction.label}
                                <CaretDown weight="bold" className="w-3 h-3" />
                            </button>
                            {showRejectDropdown && (
                                <RejectReasonMenu taxonomy={rejectTaxonomy} onSelect={handleReject} />
                            )}
                        </div>

                        <div className="relative">
                            <button
                                onClick={() => setShowSendBackDropdown(!showSendBackDropdown)}
                                disabled={busy}
                                aria-haspopup="true"
                                aria-expanded={showSendBackDropdown}
                                className={`inline-flex items-center gap-2 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                                style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                            >
                                {busyAction === 'sendback' ? (
                                    <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                                ) : null}
                                Send back for rewrite
                                <CaretDown weight="bold" className="w-3 h-3" />
                            </button>
                            {showSendBackDropdown && (
                                <RejectReasonMenu
                                    taxonomy={{ groups: (rejectTaxonomy?.groups || []).filter(g => g.attribution === 'writing_wrong'), other: null }}
                                    onSelect={handleSendBack}
                                />
                            )}
                        </div>

                        <button
                            onClick={handleSave}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                            style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {busyAction === 'save' ? (
                                <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                            ) : null}
                            {busyAction === 'save' ? 'Saving\u2026' : saveDraftAction.label}
                        </button>

                        <div className="flex-1" />

                        <button
                            onClick={handlePost}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-6 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                            style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {busyAction === 'post' ? (
                                <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Posting&hellip;</>
                            ) : (
                                <><CheckCircle weight="bold" className="w-4 h-4" /> {postAction.label}</>
                            )}
                        </button>
                    </div>
        </PostStudioCard>
    );
}

/* --------- SavedPostCard --------- */

function SavedPostCard({ post, onPosted, onRejected, onEdited, rejectTaxonomy, announce }) {
    const CARD_COLLAPSE = PS.cardCollapsible || { defaultCollapsed: true, headerMinHeight: 44, expandedShadow: true };
    const [isExpanded, setIsExpanded] = useState(!CARD_COLLAPSE.defaultCollapsed);
    const [calibration, setCalibration] = useState('agent_got_it_right');
    const [showRejectDropdown, setShowRejectDropdown] = useState(false);
    const [busy, setBusy] = useState(false);
    const [busyAction, setBusyAction] = useState(null);

    // Regeneration state
    const [modifiers, setModifiers] = useState(() => {
        const initial = {};
        REGEN_MODIFIERS.forEach(m => { initial[m.key] = REGEN_STATES.neutral; });
        return initial;
    });
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [localPostText, setLocalPostText] = useState(null);

    // Edit state — DECISION: Backend PATCH /api/linkedin-posts/{post_id}/edit exists,
    // saveLinkedInPostEdits exists in api.js. Fully wired.
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState('');
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    const postText = localPostText ?? (post.post_text || '');
    const firstComment = post.optimizer_output?.first_comment_text || post.first_comment_text;
    const postType = post.post_type || 'post';

    const postId = post._id || post.post_id;

    const hasActiveModifiers = useMemo(
        () => Object.values(modifiers).some(v => v !== REGEN_STATES.neutral),
        [modifiers]
    );

    const handleModifierChange = useCallback((key, newState) => {
        setModifiers(prev => ({ ...prev, [key]: newState }));
    }, []);

    const handleRegenerate = useCallback(async () => {
        if (!hasActiveModifiers || isRegenerating) return;
        setIsRegenerating(true);
        try {
            const result = await regenerateLinkedInPost(postId, modifiers);
            setLocalPostText(result.post_text);
            setModifiers(prev => {
                const reset = {};
                Object.keys(prev).forEach(k => { reset[k] = REGEN_STATES.neutral; });
                return reset;
            });
            const regenMsg = REGEN_CONFIG.successMessage || 'Draft updated';
            toast.success(regenMsg);
            announce(regenMsg);
        } catch (err) {
            // Spec TC-09: same status branching as Pending. Saved posts stay saved on success.
            let regenMsg;
            if (err?.status === 422) {
                regenMsg = REGEN_CONFIG.gateFailedMessage || "Draft didn't pass quality review \u2014 try different settings";
            } else if (err?.status === 400) {
                regenMsg = err.message || REGEN_CONFIG.errorMessage || 'Something went wrong \u2014 please try again';
            } else {
                regenMsg = REGEN_CONFIG.errorMessage || 'Something went wrong \u2014 please try again';
            }
            toast.error(regenMsg);
            announce(regenMsg);
        } finally {
            setIsRegenerating(false);
        }
    }, [hasActiveModifiers, isRegenerating, postId, modifiers, announce]);

    const handlePost = useCallback(async () => {
        setBusy(true);
        setBusyAction('post');
        try {
            await publishLinkedInPost(postId, calibration);
            // WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 \u2014 copy corrected ("Posted \u2014 it's live."
            // was never true; nothing here posts to LinkedIn). Deliberately NO announce() on
            // this success path: onPosted -> the page's handlePosted already announces the
            // transition and moves focus. Announcing here too would speak twice.
            toast.success(withPostedTab(PS_LABELS.postSuccess, 'Marked as published and moved to your {tab} tab.'));
            onPosted(postId);
        } catch (err) {
            // Honest message: prefer the backend's real reason (e.g. the 409
            // publishing-paused detail) over a generic "try again".
            const msg = err?.message || PS_LABELS.postError || 'Could not post \u2014 please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, calibration, onPosted, announce]);

    const handleReject = useCallback(async (reasonId, attribution) => {
        setShowRejectDropdown(false);
        setBusy(true);
        setBusyAction('reject');
        try {
            await rejectLinkedInPost(postId, reasonId, calibration, attribution);
            toast.success(PS_LABELS.rejectSuccess || 'Post rejected.');
            onRejected(postId);
        } catch (err) {
            const msg = err?.message || PS_LABELS.rejectError || 'Could not reject \u2014 please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, calibration, onRejected, announce]);

    // REDRAFT — mounted here because this is where a saved draft is read.
    //
    // WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001 (2026-07-30). The endpoint became
    // real on 2026-07-29 — it drives the post through the same pipeline the scheduler uses,
    // and restores the saved draft if the redraft fails — but it was mounted on no screen,
    // so there was nothing for the CEO to press.
    //
    // THE TRAP THIS HANDLER AVOIDS: a redraft that did NOT redraft returns HTTP 200 with
    // `status: "failed"` (or `"paused"`), carrying the real termination reason. A handler
    // that treated a 200 as success would announce "Redrafted" over work that never
    // happened — precisely what the old stub did, and what the rebuild exists to stop. So
    // the STATUS decides, never the HTTP code, and a failure shows the backend's own reason
    // rather than a soothing sentence.
    const handleRedraft = useCallback(async () => {
        setBusy(true);
        setBusyAction('redraft');
        try {
            const result = await redraftLinkedInPost(postId);
            if (result?.status === 'redrafted') {
                if (result.draft?.post_text) setLocalPostText(result.draft.post_text);
                const changed = (result.changed_fields || []).length > 0;
                const msg = changed
                    ? SHARED_UI_LABELS.redraftSuccess
                    : (result.message || SHARED_UI_LABELS.redraftNoChange);
                toast.success(msg);
                announce(msg);
                return;
            }
            // Not redrafted — paused, refused, or the council produced nothing. Report the
            // true reason. The record was already restored server-side, so the saved draft
            // the CEO is looking at is still his.
            const msg = result?.message || result?.reason || SHARED_UI_LABELS.redraftFailedFallback;
            toast.error(msg);
            announce(msg);
        } catch (err) {
            // 404 / 409 / 500 arrive here carrying the backend's detail as the message.
            const msg = err?.message || SHARED_UI_LABELS.redraftFailedFallback;
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
            setBusyAction(null);
        }
    }, [postId, announce]);

    const handleEditStart = useCallback(() => {
        setEditText(postText);
        setIsEditing(true);
    }, [postText]);

    const handleEditCancel = useCallback(() => {
        setIsEditing(false);
        setEditText('');
    }, []);

    const handleEditSave = useCallback(async () => {
        setIsSavingEdit(true);
        try {
            await saveLinkedInPostEdits(postId, editText);
            setLocalPostText(editText);
            setIsEditing(false);
            const msg = PS_LABELS.editSaveSuccess || 'Edits saved.';
            toast.success(msg);
            announce(msg);
            if (onEdited) onEdited();
        } catch (err) {
            const msg = err?.message || PS_LABELS.editSaveError || 'Could not save edits \u2014 please try again';
            toast.error(msg);
            announce(msg);
        } finally {
            setIsSavingEdit(false);
        }
    }, [postId, editText, onEdited, announce]);

    const rejectAction = findAction('reject');
    const postAction = findAction('post');

    return (
        // THE FIFTH COPY IS GONE (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 2).
        // This shell was hand-written here, byte-identically, four times. PostStudioCard's
        // `accent` and `elevated` props reproduce the exact conditional that stood here —
        // isExpanded took the border to DS_BORDERS.color and otherwise to DS_COLORS.border.brand,
        // and the shadow was raised only while expanded — so nothing the CEO can see changes.
        <PostStudioCard
            accent={isExpanded ? 'muted' : 'brand'}
            elevated={Boolean(isExpanded && CARD_COLLAPSE.expandedShadow)}
        >
            <CollapsibleCardHeader
                post={post}
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(prev => !prev)}
            />

            {isExpanded && (
                    <div className="p-6">
                        {/* Leg 4 — a saved draft keeps the check that fired on it. The CEO
                            saves a draft to come back to it, which is exactly when he needs to
                            still be able to see what was objected to. */}
                        <GateAnnotationNotice annotations={post.gate_annotations} />
                        <UnresolvedNotes notes={post.unresolved_notes} />
                        <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 mb-4 relative">
                            {isEditing ? (
                                <>
                                    <textarea
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        disabled={isSavingEdit}
                                        className="w-full text-base text-slate-800 whitespace-pre-wrap leading-relaxed bg-white border border-slate-200 rounded-lg p-3 resize-y disabled:opacity-50"
                                        style={{ minHeight: '160px' }}
                                    />
                                    <div className="flex items-center gap-3 mt-3">
                                        <button
                                            onClick={handleEditSave}
                                            disabled={isSavingEdit}
                                            className={`inline-flex items-center gap-1.5 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                                            style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                        >
                                            {isSavingEdit ? (
                                                <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Saving&hellip;</>
                                            ) : (
                                                'Save'
                                            )}
                                        </button>
                                        <button
                                            onClick={handleEditCancel}
                                            disabled={isSavingEdit}
                                            className="text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                                            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-base text-slate-800 whitespace-pre-wrap leading-relaxed">
                                        {postText}
                                    </p>
                                    <div className="absolute top-3 right-3 flex items-center gap-2">
                                        <button
                                            onClick={handleEditStart}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${INTENT_TO_BUTTON.neutral.bg} text-base font-semibold ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} transition-colors`}
                                            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                        >
                                            <PencilSimple weight="bold" className="w-4 h-4" /> Edit
                                        </button>
                                        <CopyButton text={postText} label="Copy" announce={announce} />
                                    </div>
                                </>
                            )}
                        </div>

                        {firstComment && (
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-semibold text-slate-500">First Comment</span>
                                    <CopyButton text={firstComment} label={SHARED_UI_LABELS.copyComment} announce={announce} />
                                </div>
                                <div className="bg-amber-50 rounded-lg border border-amber-100 p-3">
                                    <p className="text-sm text-slate-700">{firstComment}</p>
                                </div>
                            </div>
                        )}

                        {/* Calibration feedback pills */}
                        <div className="mb-4">
                            <p className="text-sm font-bold text-slate-700 mb-2">How did the agent do?</p>
                            <div className="flex flex-wrap gap-2">
                                {PS_CALIBRATION.map(opt => {
                                    const isSelected = calibration === opt.id;
                                    return (
                                        <button
                                            key={opt.id}
                                            onClick={() => {
                                                setCalibration(opt.id);
                                                announce((PS_LABELS.calibrationSelected || 'Marked calibration: {label}.').replace('{label}', opt.label));
                                            }}
                                            aria-pressed={isSelected}
                                            className={`px-3 py-1.5 border text-sm font-semibold transition-colors ${
                                                isSelected
                                                    ? ''
                                                    : `${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover}`
                                            }`}
                                            style={{
                                                minHeight: DS_SPACING.minTouchTarget + 'px',
                                                borderRadius: DS_BORDERS.radiusPill,
                                                ...(isSelected ? {
                                                    borderColor: DS_COLORS.border.brand,
                                                    color: DS_COLORS.text.brand,
                                                    background: DS_COLORS.surface.warm,
                                                } : {}),
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Knobs section */}
                        {REGEN_MODIFIERS.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
                                <span className="text-base font-bold text-slate-800 block mb-3">
                                    Adjust Draft
                                </span>
                                <div className="space-y-1">
                                    {REGEN_MODIFIERS.map(mod => {
                                        const isDisabled = (mod.disabled_for || []).includes(postType);
                                        return (
                                            <ModifierDial
                                                key={mod.key}
                                                label={mod.label}
                                                state={modifiers[mod.key] || REGEN_STATES.neutral}
                                                disabled={isDisabled || isRegenerating}
                                                onChange={(newState) => handleModifierChange(mod.key, newState)}
                                            />
                                        );
                                    })}
                                </div>
                                <div className="mt-4">
                                    <button
                                        disabled={!hasActiveModifiers || isRegenerating || busy}
                                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                        onClick={handleRegenerate}
                                    >
                                        {isRegenerating ? (
                                            <>
                                                <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                                                {REGEN_CONFIG.loadingLabel}
                                            </>
                                        ) : (
                                            <>
                                                <ArrowCounterClockwise weight="bold" className="w-4 h-4" />
                                                {REGEN_CONFIG.buttonLabel}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

            )}

            {/* THE ACTIONS LIVE OUTSIDE THE COLLAPSING REGION
                (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 3b).

                This bar used to sit INSIDE the `isExpanded` branch, so a collapsed card had no
                working buttons at all — the CEO had to open a card before he could act on it,
                and on a list of collapsed cards there was nothing to press. The decision does
                not depend on the body being visible, so neither should the controls. Only the
                BODY collapses now; the actions are always mounted and always operable. */}
                    {/* Action bar. Layout follows DESIGN_LANGUAGE_SKILL.md §4 "Action bar layout
                        rule": AI Action on the LEFT, Secondary and Danger in the middle, a flex
                        spacer, then Primary alone on the far right.
                        So: Redraft (AI) | Reject (Danger) | spacer | Post (Primary). */}
                    <div className="sticky bottom-0 bg-white border-t border-slate-200 px-8 py-3.5 flex items-center gap-3 z-10" style={{ boxShadow: DS_COLORS.shadow.actionBar }}>
                        {/* Redraft — the control the CEO had no way to press until now.
                            Classified AI Action per DESIGN_LANGUAGE_SKILL.md §4: it runs the
                            council, so it is violet, always icon + text, and sits at the left of
                            the bar. "One per bar" holds — the Adjust Draft regenerate control is
                            inside the card body, not this bar.
                            NOTE: the sibling regenerate control uses INTENT_TO_BUTTON.neutral,
                            which contradicts the registry. That is a PRE-EXISTING deviation and
                            is deliberately left alone; CLAUDE.md rule 32 makes the design
                            authority win over in-file precedent, so this new button follows the
                            registry rather than copying the deviation. */}
                        <button
                            onClick={handleRedraft}
                            disabled={busy}
                            data-testid="saved-redraft-button"
                            className={`inline-flex items-center gap-2 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.ai.bg} ${INTENT_TO_BUTTON.ai.text} ${INTENT_TO_BUTTON.ai.border} ${INTENT_TO_BUTTON.ai.hover} disabled:opacity-50`}
                            style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {busyAction === 'redraft' ? (
                                <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> {SHARED_UI_LABELS.redraftBusy}</>
                            ) : (
                                <><ArrowCounterClockwise weight="bold" className="w-4 h-4" /> {SHARED_UI_LABELS.redraftButton}</>
                            )}
                        </button>

                        <div className="relative">
                            <button
                                onClick={() => setShowRejectDropdown(!showRejectDropdown)}
                                disabled={busy}
                                aria-haspopup="true"
                                aria-expanded={showRejectDropdown}
                                className={`inline-flex items-center gap-2 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-50`}
                                style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                            >
                                {busyAction === 'reject' ? (
                                    <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                                ) : (
                                    <X weight="bold" className="w-4 h-4" />
                                )}
                                {rejectAction.label}
                                <CaretDown weight="bold" className="w-3 h-3" />
                            </button>
                            {showRejectDropdown && (
                                <RejectReasonMenu taxonomy={rejectTaxonomy} onSelect={handleReject} />
                            )}
                        </div>

                        <div className="flex-1" />

                        <button
                            onClick={handlePost}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-6 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                            style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {busyAction === 'post' ? (
                                <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Posting&hellip;</>
                            ) : (
                                <><CheckCircle weight="bold" className="w-4 h-4" /> {postAction.label}</>
                            )}
                        </button>
                    </div>
        </PostStudioCard>
    );
}

/* R28: a saved reason is shown by its one label from the reason vocabulary, never by its raw id. */
function reasonLabelFromTaxonomy(taxonomy, id) {
    const all = [...(taxonomy?.groups || []).flatMap(g => g.reasons || []), taxonomy?.other].filter(Boolean);
    const hit = all.find(r => r.id === id);
    return hit ? hit.label : String(id).replace(/_/g, ' ');
}

/* --------- RejectedPostCard --------- */

function RejectedPostCard({ post, onOverride, announce, rejectTaxonomy }) {
    const CARD_COLLAPSE = PS.cardCollapsible || { defaultCollapsed: true, headerMinHeight: 44, expandedShadow: true };
    const [isExpanded, setIsExpanded] = useState(!CARD_COLLAPSE.defaultCollapsed);
    const [busy, setBusy] = useState(false);

    const postText = post.post_text || '';
    const postId = post._id || post.post_id;
    const rejectedAt = post.rejected_at || post.updated_at;
    const reason = post.ceo_rejection_reason;

    const overrideAction = findAction('override');

    const handleOverride = useCallback(async () => {
        setBusy(true);
        try {
            await overrideLinkedInPost(postId);
            toast.success(PS_LABELS.overrideSuccess || 'Moved back to Saved.');
            onOverride(postId);
        } catch (err) {
            const msg = err?.message || PS_LABELS.overrideError || 'Could not override \u2014 please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setBusy(false);
        }
    }, [postId, onOverride, announce]);

    return (
        // THE FIFTH COPY IS GONE (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 2).
        // This shell was hand-written here, byte-identically, four times. PostStudioCard's
        // `accent` and `elevated` props reproduce the exact conditional that stood here —
        // isExpanded took the border to DS_BORDERS.color and otherwise to DS_COLORS.border.brand,
        // and the shadow was raised only while expanded — so nothing the CEO can see changes.
        <PostStudioCard
            accent={isExpanded ? 'muted' : 'brand'}
            elevated={Boolean(isExpanded && CARD_COLLAPSE.expandedShadow)}
        >
            <CollapsibleCardHeader
                post={post}
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(prev => !prev)}
            />

            {isExpanded && (
                    <div className="p-6">
                        {/* Rejection info */}
                        <div className="flex items-center gap-2 mb-4">
                            <XCircle weight="fill" className="w-5 h-5 text-red-500" />
                            <span className="text-sm font-semibold text-red-600">Rejected</span>
                            {rejectedAt && (
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                    <Clock weight="bold" className="w-3 h-3" />
                                    {new Date(rejectedAt).toLocaleDateString()}
                                </span>
                            )}
                            {reason && (
                                <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-600 font-medium">
                                    {reasonLabelFromTaxonomy(rejectTaxonomy, reason)}
                                </span>
                            )}
                        </div>

                        {/* Leg 4 — a rejected draft keeps its annotation too. "Which check
                            fired on the ones I turned down?" is the question the two-week
                            trial is measured on, and it cannot be asked of a blank card. */}
                        <GateAnnotationNotice annotations={post.gate_annotations} />
                        <UnresolvedNotes notes={post.unresolved_notes} />

                        {/* Post text — read-only */}
                        <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 mb-4">
                            <p className="text-base text-slate-600 whitespace-pre-wrap leading-relaxed">
                                {postText}
                            </p>
                        </div>
                    </div>

            )}

            {/* THE ACTIONS LIVE OUTSIDE THE COLLAPSING REGION
                (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 3b).

                This bar used to sit INSIDE the `isExpanded` branch, so a collapsed card had no
                working buttons at all — the CEO had to open a card before he could act on it,
                and on a list of collapsed cards there was nothing to press. The decision does
                not depend on the body being visible, so neither should the controls. Only the
                BODY collapses now; the actions are always mounted and always operable. */}
                    {/* Action bar: Override only */}
                    <div className="sticky bottom-0 bg-white border-t border-slate-200 px-8 py-3.5 flex items-center gap-3 z-10" style={{ boxShadow: DS_COLORS.shadow.actionBar }}>
                        <button
                            onClick={handleOverride}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-4 border text-base font-semibold transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                            style={{ borderRadius: DS_BORDERS.radiusButton, minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {busy ? (
                                <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                            ) : null}
                            {busy ? 'Moving\u2026' : overrideAction.label}
                        </button>
                    </div>
        </PostStudioCard>
    );
}

/* --------- HistoryCard --------- */

function HistoryCard({ post, onUnmark, announce }) {
    const CARD_COLLAPSE = PS.cardCollapsible || { defaultCollapsed: true, headerMinHeight: 44, expandedShadow: true };
    const [isExpanded, setIsExpanded] = useState(!CARD_COLLAPSE.defaultCollapsed);
    const [unmarking, setUnmarking] = useState(false);

    // WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 (CEO ruling 2026-07-17) — the undo.
    // "The button records his statement, it doesn't verify LinkedIn. But make it undoable."
    // Announce on FAILURE only, mirroring handlePost/handleOverride: on success the page's
    // handleUnmark announces the transition and moves focus, so announcing here too would
    // speak twice. On failure the page handler never runs, so this is the only voice —
    // and it uses the repeat-safe announcer (#1006), so a second failed attempt returning
    // the byte-identical reason still speaks.
    const handleUnmark = useCallback(async () => {
        setUnmarking(true);
        try {
            await unmarkLinkedInPost(post._id || post.post_id);
            toast.success(PS_LABELS.unmarkSuccess || 'Moved back to Saved — no longer marked as published.');
            onUnmark(post._id || post.post_id);
        } catch (err) {
            // Prefer the backend's real reason (e.g. the 409 "not marked" detail) over a
            // generic retry line — an honest refusal is more useful than a shrug.
            const msg = err?.message || PS_LABELS.unmarkError || 'Could not undo — please try again.';
            toast.error(msg);
            announce(msg);
        } finally {
            setUnmarking(false);
        }
    }, [post, onUnmark, announce]);

    // DECISION: content_type absent on legacy records — treat as 'text' per field contract.
    const contentType = post.content_type || LINKEDIN_CONTENT_TYPES.TEXT;
    const isVideo = contentType === LINKEDIN_CONTENT_TYPES.VIDEO;

    const postText = post.post_text || '';
    // linkedin_post_url: present on video records; may also exist on text records (save url command).
    const linkedInUrl = post.linkedin_post_url || null;
    const publishedAt = post.published_at;
    const calibrationLabel = post.ceo_calibration;
    // WI-LINKEDIN-POST-FRONTEND-V1 — manually-captured LinkedIn metrics from the
    // Telegram performance flow. Null when no capture has happened yet for this
    // post; em-dash placeholder so the row is always visible (a consistent
    // surface to remind the CEO that metrics can be added).
    const metrics = {
        Impressions: post.impressions,
        Reactions: post.reactions,
        Comments: post.comments,
        Saves: post.saves,
    };
    // RC-1: check all 8 writer-settable fields so the "add metrics" prompt hides
    // correctly once any metric (incl. video-only ones) has been captured.
    const hasAnyMetric = Object.values(metrics).some(v => typeof v === 'number')
        || typeof post.reposts === 'number'
        || typeof post.sends_on_linkedin === 'number'
        || typeof post.video_views === 'number'
        || typeof post.average_watch_time_seconds === 'number';

    return (
        // THE FIFTH COPY IS GONE (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 2).
        // This shell was hand-written here, byte-identically, four times. PostStudioCard's
        // `accent` and `elevated` props reproduce the exact conditional that stood here —
        // isExpanded took the border to DS_BORDERS.color and otherwise to DS_COLORS.border.brand,
        // and the shadow was raised only while expanded — so nothing the CEO can see changes.
        <PostStudioCard
            accent={isExpanded ? 'muted' : 'brand'}
            elevated={Boolean(isExpanded && CARD_COLLAPSE.expandedShadow)}
        >
            <CollapsibleCardHeader
                post={post}
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(prev => !prev)}
            />

            {isExpanded && (
                <div className="p-6">
                    {/* Published info */}
                    <div className="flex items-center gap-2 mb-4">
                        <CheckCircle weight="fill" className="w-5 h-5" style={{ color: DS_COLORS.text.success }} />
                        <span className="text-sm font-semibold" style={{ color: DS_COLORS.text.success }}>You marked this published</span>
                        {publishedAt && (
                            <span className="text-xs flex items-center gap-1" style={{ color: DS_COLORS.text.muted }}>
                                <Clock weight="bold" className="w-3 h-3" />
                                {new Date(publishedAt).toLocaleString()}
                            </span>
                        )}
                        {calibrationLabel && (
                            <span className={`text-xs px-2 py-1 rounded-full ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} font-medium`}>
                                {calibrationLabel.replace(/_/g, ' ')}
                            </span>
                        )}
                        {post.performance_tier && (
                            <span
                                className="text-sm font-medium px-2 py-0.5 rounded ml-2"
                                style={{
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    marginLeft: '0.5rem',
                                    backgroundColor:
                                        post.performance_tier === 'OUTPERFORMED' ? '#d1fae5' :
                                        post.performance_tier === 'UNDERPERFORMED' ? '#fee2e2' :
                                        '#f3f4f6',
                                    color:
                                        post.performance_tier === 'OUTPERFORMED' ? '#065f46' :
                                        post.performance_tier === 'UNDERPERFORMED' ? '#991b1b' :
                                        '#374151',
                                }}
                            >
                                {post.performance_tier}
                            </span>
                        )}
                    </div>

                    {/* LinkedIn performance metrics — manually captured via the
                        Telegram flow (WI-LINKEDIN-POST-TELEGRAM-PERF-V1). The row
                        always renders so the absence of metrics is itself visible. */}
                    <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(metrics).map(([label, value]) => (
                            <div
                                key={label}
                                className="rounded-lg border p-3"
                                style={{
                                    borderColor: DS_BORDERS.color,
                                    background: DS_COLORS.surface.warm,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel?.fontFamily,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: DS_COLORS.text.secondary,
                                    }}
                                >
                                    {label}
                                </div>
                                <div
                                    className="mt-1"
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.body?.fontFamily,
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: typeof value === 'number' ? DS_COLORS.text.primary : DS_COLORS.text.muted,
                                    }}
                                >
                                    {typeof value === 'number' ? value.toLocaleString() : '—'}
                                </div>
                            </div>
                        ))}
                        {/* RC-1: Reposts — all post types, tile only when value is present */}
                        {typeof post.reposts === 'number' && (
                            <div
                                className="rounded-lg border p-3"
                                style={{
                                    borderColor: DS_BORDERS.color,
                                    background: DS_COLORS.surface.warm,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel?.fontFamily,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: DS_COLORS.text.secondary,
                                    }}
                                >
                                    Reposts
                                </div>
                                <div
                                    className="mt-1"
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.body?.fontFamily,
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: DS_COLORS.text.primary,
                                    }}
                                >
                                    {post.reposts.toLocaleString()}
                                </div>
                            </div>
                        )}
                        {/* RC-1: Sends — all post types, tile only when value is present */}
                        {typeof post.sends_on_linkedin === 'number' && (
                            <div
                                className="rounded-lg border p-3"
                                style={{
                                    borderColor: DS_BORDERS.color,
                                    background: DS_COLORS.surface.warm,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel?.fontFamily,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: DS_COLORS.text.secondary,
                                    }}
                                >
                                    Sends
                                </div>
                                <div
                                    className="mt-1"
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.body?.fontFamily,
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: DS_COLORS.text.primary,
                                    }}
                                >
                                    {post.sends_on_linkedin.toLocaleString()}
                                </div>
                            </div>
                        )}
                        {/* RC-1: Video Views — video posts only, tile only when value is present */}
                        {isVideo && typeof post.video_views === 'number' && (
                            <div
                                className="rounded-lg border p-3"
                                style={{
                                    borderColor: DS_BORDERS.color,
                                    background: DS_COLORS.surface.warm,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel?.fontFamily,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: DS_COLORS.text.secondary,
                                    }}
                                >
                                    Video Views
                                </div>
                                <div
                                    className="mt-1"
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.body?.fontFamily,
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: DS_COLORS.text.primary,
                                    }}
                                >
                                    {post.video_views.toLocaleString()}
                                </div>
                            </div>
                        )}
                        {/* RC-1: Watch Time — video posts only, rendered with seconds unit (e.g. 12s) */}
                        {isVideo && typeof post.average_watch_time_seconds === 'number' && (
                            <div
                                className="rounded-lg border p-3"
                                style={{
                                    borderColor: DS_BORDERS.color,
                                    background: DS_COLORS.surface.warm,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel?.fontFamily,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: DS_COLORS.text.secondary,
                                    }}
                                >
                                    Watch Time
                                </div>
                                <div
                                    className="mt-1"
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.body?.fontFamily,
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: DS_COLORS.text.primary,
                                    }}
                                >
                                    {post.average_watch_time_seconds}s
                                </div>
                            </div>
                        )}
                    </div>
                    {!hasAnyMetric && (
                        <p className="mb-4 text-xs" style={{ color: DS_COLORS.text.muted }}>
                            {(PS_LABELS.postedMetricsCapturePrompt || '').replace(
                                '{topic}',
                                post.source_article_title?.slice(0, 30) || 'this post',
                            )}
                        </p>
                    )}
                    {post.pattern_findings_generated_at && (
                        <div className="text-xs text-gray-400 mt-1 mb-4" style={{ fontSize: '0.95rem' }}>
                            Pattern analysis: {daysSince(post.pattern_findings_generated_at)} days ago
                            {daysSince(post.pattern_findings_generated_at) > 14 && (
                                <span className="text-amber-500 ml-1" style={{ color: '#d97706', marginLeft: '0.25rem' }}>· may be stale</span>
                            )}
                        </div>
                    )}

                    {/* Post body — read-only.
                        Video records carry null post_text; show the topic title + clickable URL
                        as the body so the card is never blank. Text records show the post copy
                        as usual. Source: VIDEO_CARD_TITLE_DESIGN_REVIEW_2026-05-31.md §5. */}
                    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                        {isVideo && !postText ? (
                            <div>
                                {/* Topic title — read-only, byte-identical to source_article_title
                                    so learning_signals / performance_writer matching is unaffected.
                                    DECISION: no title-case transform (design review binding condition). */}
                                {post.source_article_title && (
                                    <p className="text-base font-semibold text-slate-800 mb-3 leading-relaxed">
                                        {post.source_article_title}
                                    </p>
                                )}
                                {linkedInUrl && (
                                    <a
                                        href={linkedInUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-base font-semibold underline"
                                        style={{
                                            color: DS_COLORS.text.brand,
                                            minHeight: DS_SPACING.minTouchTarget + 'px',
                                            lineHeight: DS_SPACING.minTouchTarget + 'px',
                                        }}
                                        aria-label={SHARED_UI_LABELS.linkedInViewLinkAria}
                                    >
                                        View on LinkedIn
                                    </a>
                                )}
                            </div>
                        ) : (
                            <>
                                <p className="text-base text-slate-600 whitespace-pre-wrap leading-relaxed">
                                    {postText}
                                </p>
                                {/* Render URL link for text records that carry one (e.g. save url command). */}
                                {linkedInUrl && (
                                    <div className="mt-3">
                                        <a
                                            href={linkedInUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-base font-semibold underline"
                                            style={{
                                                color: DS_COLORS.text.brand,
                                                minHeight: DS_SPACING.minTouchTarget + 'px',
                                                lineHeight: DS_SPACING.minTouchTarget + 'px',
                                            }}
                                            aria-label={SHARED_UI_LABELS.linkedInViewLinkAria}
                                        >
                                            View on LinkedIn
                                        </a>
                                    </div>
                                )}
                            </>
                        )}

                    </div>
                </div>
            )}

            {/* Undo the mark — WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001.
                Named "Unmark as published" rather than "Undo": a screen-reader
                user tabbing the Posted tab hears the button out of context, and
                "Undo" alone does not say what it undoes. minTouchTarget keeps it
                at the >=44px the design system requires.

                WI-IDEA-FULL-READ-AND-CONFIRM-IN-FLIGHT-001 Leg 2: this sits OUTSIDE the
                {isExpanded && …} region. It used to be the last element INSIDE a 317-line
                collapsing body, so it vanished the moment the card was closed — and it is
                BOOKKEEPING, not one of the three decisions, so it should never have been
                gated behind reading the whole post. Same rule the other four tabs already
                follow: an action bar stays live whether the card is open or closed.
                A pure move — the button depends on nothing computed inside the expanded
                region (handleUnmark, unmarking and every token are component/module scope). */}
            <div className="px-6 pb-6 pt-4" style={{ borderTop: DS_BORDERS.width + ' solid ' + DS_BORDERS.color }}>
                <button
                    type="button"
                    onClick={handleUnmark}
                    disabled={unmarking}
                    className="inline-flex items-center justify-center px-4 rounded-lg text-base font-semibold disabled:opacity-60"
                    style={{
                        minHeight: DS_SPACING.minTouchTarget + 'px',
                        border: DS_BORDERS.width + ' solid ' + DS_BORDERS.color,
                        color: DS_COLORS.text.primary,
                        background: DS_COLORS.surface.warm,
                    }}
                >
                    {unmarking
                        ? (PS_LABELS.unmarkBusy || 'Undoing…')
                        : (PS_LABELS.unmarkLabel || 'Unmark as published')}
                </button>
            </div>
        </PostStudioCard>
    );
}

/* --------- Main Page --------- */

export default function LinkedInPostAgent() {
    const [activeTab, setActiveTab] = useState(PS_TABS[0].id);
    const [drafts, setDrafts] = useState([]);
    const [saved, setSaved] = useState([]);
    const [posted, setPosted] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [loading, setLoading] = useState(true);
    // The idea surface, folded in from the standalone /ideas screen. The PAGE owns this
    // fetch, not the tab body, so the Ideas count is correct before the tab is ever
    // opened — a count that waited for its own tab to be clicked would read as unknown
    // (or worse, zero) until then.
    const [ideas, setIdeas] = useState([]);
    const [ideaSummary, setIdeaSummary] = useState(null);
    // Which lists could not be loaded this pass. A tab in here reports UNKNOWN rather
    // than 0 — "we could not look" must never render as "there is nothing".
    const [loadFailed, setLoadFailed] = useState({});
    // Idea-row decisions (WI-IDEA-RERUN-AND-ACTIONABLE-IDEA-TAB-001). `busyIdeaId` disables one
    // row's controls while its decision is in flight.
    //
    // `rejectingIdea` USED TO LIVE HERE and is deliberately gone
    // (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 3a). Holding "which card is being
    // rejected" on the PAGE is what put the menu at the top of the tab while the card kept its
    // own button below — the two-places-at-once defect. A dropdown anchored to its own trigger
    // already knows which card it belongs to, so the state has nothing left to hold. Reject
    // still refuses an empty reason; the reason is now collected by the card's own menu before
    // the decision is ever sent.
    const [busyIdeaId, setBusyIdeaId] = useState(null);
    // Finished drafts the archive flag has removed from Pending. Held apart from `drafts` on
    // purpose: they are not pending, so they must not be counted as if they were.
    const [withheldDrafts, setWithheldDrafts] = useState([]);
    // WI-ONE-POST-REACHES-THE-CEO-001 Leg 3 — the DESTINATION for a decided idea.
    // CEO ruling D-2026-07-31-15 permits sub-tabs under REJECTED, ideas and posts, and
    // nowhere else. `posts` is first because it is the list that was already there; the
    // Rejected tab a returning reader knows opens on exactly what it opened on before.
    const [rejectedIdeas, setRejectedIdeas] = useState([]);
    const [rejectedSubTab, setRejectedSubTab] = useState(REJECTED_SUBTABS[0].id);
    // THE IDEAS TAB'S OWN SUB-TABS — CEO ruling D-2026-08-06-1, which supersedes
    // D-2026-07-31-15's "no other sub-tabs" clause FOR THIS TAB ONLY (see
    // docs/handoffs/SESSION_HANDOFF_DECISIONS_2026-08-06_TB.md). The groups were previously
    // stacked down one screen; an idea now appears in exactly one sub-tab.
    //
    // Deliberately NOT seeded from a hardcoded id list: the groups are published by
    // tab_definitions.py and the renderer must never carry its own idea of what they are —
    // that hand-written membership rule is the exact thing tab_definitions exists to abolish.
    // Empty string means "not chosen yet"; the render resolves it against the groups that
    // actually arrived, so a renamed or reordered group can never leave a dead selection.
    const [ideaSubTab, setIdeaSubTab] = useState('');
    // WI-LINKEDIN-REJECT-REASONS-AND-LEARNING-LOOP-001: grouped reject taxonomy,
    // fetched at runtime so both reject surfaces stay in sync with the backend
    // authority. Empty default renders no menu items rather than crashing.
    const [rejectTaxonomy, setRejectTaxonomy] = useState({ groups: [], other: null });

    // WI-LINKEDIN-SAVE-BUTTON-LIVE-FAILURE-001 — screen-reader announcer + focus
    // target. Post Studio surfaced action outcomes and tab changes only through a
    // visual toast + tab highlight, which a screen reader never announces — so the
    // CEO (a screen-reader user) could not tell that reconsider/save did anything
    // and never reached the action. This polite live region announces each
    // outcome + destination, and contentRef lands focus on the new view.
    // WI-POST-BUTTON-REGRESSION-HUNT-001 — announcing through useAnnouncer, not a
    // bare setState. Publishing is hard-paused, so every Post press returns the
    // identical 409 reason; a bare setState to an equal value is a React no-op, so
    // presses 2..n changed no DOM and were never spoken. See the hook for why.
    const [statusMessage, announce] = useAnnouncer();
    const contentRef = useRef(null);
    const focusContent = useCallback(() => {
        // Defer to after the destination content renders.
        requestAnimationFrame(() => { contentRef.current?.focus(); });
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [draftRes, savedRes, postedRes, rejectedRes, rejectReasonsRes, ideaRes, rejectedIdeaRes] = await Promise.allSettled([
                getDraftLinkedInPosts(),
                getSavedLinkedInPosts(),
                getPublishedLinkedInPosts(),
                getRejectedLinkedInPosts(),
                getLinkedInPostRejectReasons(),
                getIdeaQueue(),
                getRejectedIdeas(),
            ]);
            if (draftRes.status === 'fulfilled') {
                setDrafts(draftRes.value?.posts || []);
                // Withheld drafts travel separately and are NOT folded into `drafts` — an
                // archived draft is not pending, so counting it would mis-state the tab. It is
                // shown, named, with its reason, rather than disappearing.
                setWithheldDrafts(draftRes.value?.withheld || []);
            }
            if (savedRes.status === 'fulfilled') setSaved(savedRes.value?.posts || []);
            if (postedRes.status === 'fulfilled') setPosted(postedRes.value?.posts || []);
            if (rejectedRes.status === 'fulfilled') setRejected(rejectedRes.value?.posts || []);
            if (ideaRes.status === 'fulfilled') {
                setIdeas(ideaRes.value?.ideas || []);
                setIdeaSummary(ideaRes.value || null);
            }
            if (rejectedIdeaRes.status === 'fulfilled') setRejectedIdeas(rejectedIdeaRes.value?.ideas || []);
            // Record every list that did NOT come back. allSettled means a rejected fetch
            // silently leaves its array at the previous value, which on first load is []
            // — so without this the tab count reported 0 for an endpoint that never
            // answered. Each tab is settled independently: one dead list cannot blank
            // the others.
            setLoadFailed({
                pending: draftRes.status !== 'fulfilled',
                saved: savedRes.status !== 'fulfilled',
                posted: postedRes.status !== 'fulfilled',
                rejected: rejectedRes.status !== 'fulfilled',
                idea: ideaRes.status !== 'fulfilled',
                // Settled independently, like every sibling above: a dead decided-ideas
                // list must never blank the Rejected tab's posts.
                rejectedIdeas: rejectedIdeaRes.status !== 'fulfilled',
            });
            if (rejectReasonsRes.status === 'fulfilled') {
                setRejectTaxonomy({
                    groups: rejectReasonsRes.value?.groups || [],
                    other: rejectReasonsRes.value?.other || null,
                });
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // RC-2: refetch the posted list each time the Posted tab becomes active.
    // Cowork writes metrics AFTER the CEO opens the page; this ensures the
    // tile values are current without a manual reload.
    // Deps are honest — loadAll is stable (useCallback []), activeTab changes
    // only on user action. No polling, no setInterval, no infinite loop.
    useEffect(() => {
        if (activeTab === 'posted') {
            loadAll();
        }
    }, [activeTab, loadAll]);

    const handlePosted = useCallback((postId) => {
        setDrafts(prev => prev.filter(p => (p._id || p.post_id) !== postId));
        setSaved(prev => prev.filter(p => (p._id || p.post_id) !== postId));
        setActiveTab('posted');
        announce(withPostedTab(PS_LABELS.announcePosted, 'Marked as published and moved to your {tab} tab.'));
        focusContent();
        loadAll();
    }, [loadAll, announce, focusContent]);

    // WI-POST-MARKS-PUBLISHED-BOOKKEEPING-001 — the undo's page-level transition. Mirrors
    // handleOverride exactly (that one moves a REJECTED post back; this one a MARKED post),
    // so the undo announces and moves focus the same way every other tab transition does.
    const handleUnmark = useCallback((postId) => {
        setPosted(prev => prev.filter(p => (p._id || p.post_id) !== postId));
        setActiveTab('saved');
        announce(PS_LABELS.announceUnmarked);
        focusContent();
        loadAll();
    }, [loadAll, announce, focusContent]);

    const handleRejected = useCallback((postId) => {
        setDrafts(prev => prev.filter(p => (p._id || p.post_id) !== postId));
        setSaved(prev => prev.filter(p => (p._id || p.post_id) !== postId));
        setActiveTab('rejected');
        announce(PS_LABELS.announceRejected);
        focusContent();
        loadAll();
    }, [loadAll, announce, focusContent]);

    const handleSaved = useCallback((post) => {
        setDrafts(prev => prev.filter(p =>
            (p._id || p.post_id) !== (post._id || post.post_id)
        ));
        setSaved(prev => [post, ...prev]);
        setActiveTab('saved');
        announce(PS_LABELS.announceSaved);
        focusContent();
        loadAll();
    }, [loadAll, announce, focusContent]);

    const handleOverride = useCallback((postId) => {
        setRejected(prev => prev.filter(p => (p._id || p.post_id) !== postId));
        setActiveTab('saved');
        announce(PS_LABELS.announceOverride);
        focusContent();
        loadAll();
    }, [loadAll, announce, focusContent]);

    // A CEO decision on one idea row. Approve and promote need no reason and are recorded
    // straight away; reject needs a structured reason, so it opens the SAME reject menu the
    // other tabs already use rather than inventing a second vocabulary.
    //
    // Honesty: the backend refuses a second decision with a 409 and refuses reject with an
    // empty reason. Both surface the backend's own message. Nothing reports success it did not
    // get — the list is reloaded from the server afterwards, so what the CEO sees is what was
    // actually recorded, not what was requested.
    // Leg 3a: reject no longer detours through a page-level panel. The card's own menu has
    // ALREADY collected the structured reason by the time this is called, so all three
    // decisions are recorded the same way, from the same place, with no intermediate state.
    const handleIdeaDecision = useCallback(async (idea, decision, reasonId) => {
        setBusyIdeaId(idea.post_id);
        try {
            if (decision === 'reject') {
                // Reject still goes through rejectCampaignTask with a structured reason — the
                // endpoint and the vocabulary are unchanged. Only the control moved.
                await rejectCampaignTask(idea.post_id, reasonId);
            } else {
                await decideIdeaCard(idea.post_id, decision);
            }
            const msg = decision === 'promote'
                ? IDEA_QUEUE_LABELS.statusPromoted
                : decision === 'reject'
                    ? IDEA_QUEUE_LABELS.statusRejected
                    : IDEA_QUEUE_LABELS.statusApproved;
            toast.success(msg);
            announce(msg);
            await loadAll();
        } catch (err) {
            const msg = err?.message || IDEA_QUEUE_LABELS.decideFailed;
            toast.error(msg);
            announce(msg);
        } finally {
            setBusyIdeaId(null);
        }
    }, [announce, loadAll]);

    // `handleIdeaReject` was a SECOND decision path that existed only to service the page-level
    // reject panel (Leg 3a). It has been folded into handleIdeaDecision above, so all three
    // decisions — approve, promote, reject — now travel one path with one busy state and one
    // announcement. Two paths for one act is how the announcement and the recorded outcome
    // drift apart; there is now nothing to drift.

    // null means UNKNOWN — the list could not be loaded. It is NOT 0. TabChips renders the
    // word for a null and shows no badge for a real 0.
    const tabCounts = useMemo(() => ({
        idea: loadFailed.idea ? null : countIdeasThatNeedHim(ideas, ideaSummary),
        pending: loadFailed.pending ? null : drafts.length,
        saved: loadFailed.saved ? null : saved.length,
        posted: loadFailed.posted ? null : posted.length,
        rejected: loadFailed.rejected ? null : rejected.length,
    }), [
        ideas, ideaSummary, drafts.length, saved.length, posted.length, rejected.length,
        loadFailed.idea, loadFailed.pending, loadFailed.saved, loadFailed.posted,
        loadFailed.rejected,
    ]);

    // WI-LINKEDIN-SAVE-BUTTON-LIVE-FAILURE-001 — a manual tab switch announces the
    // destination + count and lands focus on the panel, so a screen-reader user
    // perceives the change instead of a silent visual highlight.
    //
    // A COUNT THAT HAS NOT ARRIVED IS NOT ZERO
    // (WI-ONE-CARD-EVERY-TAB-COPY-SAVED-AND-DELETE-THE-COPIES-001 leg 4).
    //
    // The spoken line was decided at CLICK time and never corrected. Landing on Ideas before the
    // queue resolved announced "Showing Ideas, 0 ideas" and went on saying zero after six cards
    // arrived — measured live on 2026-07-31, still reading 0 at nine seconds with the badge
    // reading 6. On screen the badge corrects itself; BY EAR it never did, and the CEO reads
    // this surface by text-to-speech. He would be told nothing waits on the tab where six
    // decisions wait on him.
    //
    // Same class as the office inbox claiming it was clear when it never got an answer, and the
    // SAME shape is reused rather than a second mechanism invented: `null` already means UNKNOWN
    // here and already has a spoken word. While the lists are still loading the count is not
    // known, so it is reported unknown instead of zero.
    const spokenCount = useCallback((tabId) => {
        const tab = PS_TABS.find(t => t.id === tabId);
        const count = loading ? null : tabCounts[tabId];
        return count == null
            ? `Showing ${tab?.label || tabId}, ${SHARED_UI_LABELS.countUnknownSpoken}.`
            : `Showing ${tab?.label || tabId}, ${count} ${tabCountNoun(tab, count)}.`;
    }, [loading, tabCounts]);

    const handleTabChange = useCallback((tabId) => {
        setActiveTab(tabId);
        announce(spokenCount(tabId));
        focusContent();
    }, [spokenCount, announce, focusContent]);

    // ...and when the count DOES arrive, say it. A one-shot announcement at click time is what
    // let the wrong number stand for the rest of the session; the correction is the other half.
    //
    // SCOPED TO THE FIRST LOAD ONLY, and that is deliberate. `loadAll` also re-runs after every
    // action, so announcing on every completion would CLOBBER the message the action just made —
    // caught by the existing suite, which found "Marked as published and moved to your Posted
    // tab." replaced by "Showing Posted, 0 posts.". The defect being fixed is the OPENING load,
    // when the answer had not arrived yet; an action's own outcome must always win after that.
    const correctedFirstLoad = useRef(false);
    useEffect(() => {
        if (loading || correctedFirstLoad.current) return;
        correctedFirstLoad.current = true;
        announce(spokenCount(activeTab));
    }, [loading, activeTab, spokenCount, announce]);

    const renderTabContent = (tabId) => {
        // The idea surface, moved here from the standalone /ideas screen. Shows EVERY card
        // — the ones frozen before the claim repair and the ones minted after it — in one
        // unfiltered list, each row saying in plain words where its claim came from.
        if (tabId === 'idea') {
            // RESOLVE THE SELECTION AGAINST THE GROUPS THAT ACTUALLY ARRIVED.
            //
            // Never trust the stored id on its own. The groups are published by
            // tab_definitions.py, so one can be renamed, reordered or removed by a backend
            // change alone — and a selection pointing at a group that is no longer there would
            // render NOTHING while the tab looked fine, which is this lane's signature failure
            // (a screen that is confidently empty). Falling back to the first group means the
            // worst case is "you are looking at a different sub-tab than you were", never "the
            // tab is blank and nobody can tell why".
            const ideaGroups = ideaSummary?.groups || [];
            const activeIdeaGroup =
                ideaGroups.find(g => g.id === ideaSubTab) || ideaGroups[0] || null;
            return (
                <>
                    {/* WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001 item B (CEO ruling 2026-07-30).
                        This block previously read "This is the SAME menu the other tabs use —
                        one vocabulary, not two", and rendered <RejectReasonMenu> fed the
                        DRAFT-stage taxonomy. The claim was inverted. A pre-draft idea card
                        speaks its own seven-code vocabulary (IDEA_CARD_CONTRACT §4), DISJOINT
                        from the draft-stage codes — no code appears in both — and the backend
                        refuses anything outside its own set with a 400. So every reject fired
                        from this tab was refused, and four cards the CEO wanted to clear could
                        not be cleared.

                        The fix is not a second list. IdeaCardRejectMenu is the ONE menu, with
                        the ONE fetcher, that the Work Inbox card already used — now shared, so
                        both surfaces read the same backend authority
                        (linkedin_post_rules.json::idea_card_reject_taxonomy). Nothing here
                        names a code or a label. */}
                    {/* THE PAGE-LEVEL REJECT PANEL IS GONE
                        (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 3a).

                        A panel rendered HERE — above the whole list — while the card that was
                        being rejected kept its own Reject button below it. That is the "renders
                        in two places at once" the CEO read off his own screen. It also had to
                        re-state the card's claim as text purely to say WHICH card it meant,
                        which is the tell that it was in the wrong place.

                        The menu now lives inside its card (IdeaTabPanel), anchored to its own
                        trigger. It is the SAME shared IdeaCardRejectMenu with the SAME single
                        fetcher and the same backend vocabulary — nothing about the reject
                        vocabulary changed, only where the control lives. The `rejectingIdea`
                        state and its cancel affordance go with it: a dropdown dismisses itself,
                        so neither has anything left to do. */}
                    {/* TWO GROUPS, ONE TAB (WI-THE-MAP-IS-THE-TRUTH-IDEA-LANE-001 Task 2).

                        A GROUP IS NOT A SUB-TAB — the ruling permits sub-tabs under REJECTED
                        only. This is one list with two labelled sections, which is what makes
                        it legal to show the drafting queue here at all.

                        WHY THE SECOND GROUP EXISTS. By the CEO's own definition this tab holds
                        "ideas awaiting YOUR decision", so an approved idea does not belong in
                        the first group. But removing it from the screen entirely is exactly how
                        the Meesho card sat approved and undrafted for THIRTEEN DAYS with no
                        surface anywhere saying so. It is decided, it is not his to act on, and
                        he still has to be able to see it — hence a named group that says
                        nothing is needed from him.

                        Every label and sentence below comes from the backend definition
                        (tab_definitions.py), not from this file. The tab cannot describe itself
                        differently from the set it is actually showing. */}
                    {/* DEGRADE TO VISIBLE, NEVER TO EMPTY.

                        Found by the existing suites, not by reading: mapping over
                        `groups` alone renders NOTHING when the response does not carry
                        them — an older cached payload, a partial failure, a rollback.
                        A tab that silently goes blank is the worst outcome available
                        here, and it is the failure class this lane keeps producing.

                        So when the definition does not arrive, fall back to the flat
                        list exactly as it rendered before. Fewer labels, every card
                        still on screen. */}
                    {!(ideaSummary?.groups || []).length && (
                        <IdeaTabPanel
                            ideas={ideas}
                            summary={ideaSummary}
                            loadFailed={!!loadFailed.idea}
                            onDecide={handleIdeaDecision}
                            busyId={busyIdeaId}
                        />
                    )}
                    {/* THREE SUB-TABS, NOT THREE STACKED SECTIONS — CEO ruling D-2026-08-06-1.
                        "A smaller version of the tabs across the top", which is meant literally:
                        this is the SAME TabChips component the top-level tabs and the REJECTED
                        sub-tabs use, given a different `tabs` array. A hand-rolled second chip
                        row is how the "unknown is not zero" ruling gets dropped — it was dropped
                        exactly that way in the first draft of the Rejected sub-tabs, and the
                        comment there records it.

                        AN IDEA APPEARS IN EXACTLY ONE SUB-TAB, which is only true because the
                        three group selectors are a genuine partition (verified live: all six
                        pairwise intersections empty). Built in this order deliberately —
                        exclusive sub-tabs over records genuinely in two states at once would
                        HIDE an overlap instead of fixing it. */}
                    {(ideaSummary?.groups || []).length > 0 && (
                        <TabChips
                            activeTab={activeIdeaGroup?.id}
                            onTabChange={(id) => {
                                setIdeaSubTab(id);
                                // Spoken, not merely highlighted. This surface is read by
                                // text-to-speech and a highlight alone is silent.
                                announce(
                                    (ideaSummary.groups.find(g => g.id === id) || {}).label || id
                                );
                            }}
                            /* UNKNOWN IS NOT ZERO (CEO ruling 2026-07-30). A failed idea load
                               must say so rather than render a confident "(0)" — the lie that
                               ruling exists to prevent. */
                            tabCounts={Object.fromEntries(
                                ideaSummary.groups.map(g => [g.id, loadFailed.idea ? null : g.count])
                            )}
                            tabs={ideaSummary.groups.map(g => ({ id: g.id, label: g.label }))}
                            ariaLabel={IDEA_SUBTAB_ARIA_LABEL}
                            variant="tint"
                        />
                    )}
                    {(activeIdeaGroup ? [activeIdeaGroup] : []).map(group => {
                        // THE ROWS COME WITH THE GROUP (WI-WITHHELD-CARD-ROUTE-BACK-001).
                        //
                        // This was `group.id === 'queued_for_drafting' ? queued : ideas` — a
                        // hand-written membership rule in the renderer, which is the thing
                        // tab_definitions.py exists to abolish. It does not merely fail to
                        // handle a third group; it MIS-handles it, silently pouring the
                        // awaiting-you cards under whatever new heading the backend sends.
                        // The backend now publishes each group's own rows, so the guess goes.
                        //
                        // The fallback keeps a cached older payload rendering rather than
                        // blanking the tab, and is deliberately the OLD rule so behaviour is
                        // identical for the two groups that predate this change.
                        const rows = Array.isArray(group.rows)
                            ? group.rows
                            : (group.id === 'queued_for_drafting' ? (ideaSummary?.queued || []) : ideas);
                        return (
                            <section key={group.id} style={{ marginBottom: DS_SPACING.cardGap }}>
                                <h3
                                    className="font-bold"
                                    style={{
                                        ...DS_TYPOGRAPHY.listItemTitle,
                                        color: DS_COLORS.text.primary,
                                        marginBottom: 4,
                                    }}
                                >
                                    {group.label} ({group.count})
                                </h3>
                                <p
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                        color: DS_COLORS.text.secondary,
                                        marginBottom: 12,
                                    }}
                                >
                                    {group.holds}
                                </p>
                                <IdeaTabPanel
                                    ideas={rows}
                                    summary={ideaSummary}
                                    loadFailed={!!loadFailed.idea}
                                    statusLine={
                                        group.id === 'queued_for_drafting' && ideaSummary?.oldest_awaiting_days != null
                                            ? PS_LABELS.queuedOldest
                                                ? PS_LABELS.queuedOldest.replace('{days}', ideaSummary.oldest_awaiting_days)
                                                : `The oldest has been queued ${ideaSummary.oldest_awaiting_days} days.`
                                            : ' '
                                    }
                                    emptyText={group.empty_sentence}
                                    /* A queued card is not his to act on — offering him
                                       buttons on it would be a control that does nothing. */
                                    onDecide={group.id === 'queued_for_drafting' ? null : handleIdeaDecision}
                                    busyId={busyIdeaId}
                                />
                                {/* APPROVED IDEAS THAT LEFT THE QUEUE WITHOUT A DRAFT (26 Oct 2026). Shown under the queue, listed and counted apart from it, with
                                    the reason each stopped. Words only: no decision buttons, because there is nothing for him to decide. */}
                                {group.id === 'queued_for_drafting' && ideaSummary?.left_the_queue && (
                                    ideaSummary.left_the_queue.load_failed ? (
                                        <p data-testid="left-the-queue-failed" role="status" style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary, marginTop: 16 }}>
                                            {IDEA_QUEUE_LABELS.leftQueueFailed}
                                        </p>
                                    ) : (ideaSummary.left_the_queue.count || 0) > 0 ? (
                                        <div data-testid="left-the-queue" style={{ marginTop: 24 }}>
                                            <h3 className="font-bold" style={{ ...DS_TYPOGRAPHY.listItemTitle, color: DS_COLORS.text.primary, marginBottom: 4 }}>
                                                {ideaSummary.left_the_queue.label} ({ideaSummary.left_the_queue.count})
                                            </h3>
                                            <p style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily, fontSize: DS_TYPOGRAPHY.bodyMd.fontSize, color: DS_COLORS.text.secondary, marginBottom: 12 }}>
                                                {ideaSummary.left_the_queue.holds}
                                            </p>
                                            <IdeaTabPanel
                                                ideas={ideaSummary.left_the_queue.rows || []}
                                                summary={ideaSummary}
                                                loadFailed={false}
                                                statusLine=" "
                                                emptyText=""
                                                onDecide={null}
                                                busyId={busyIdeaId}
                                            />
                                        </div>
                                    ) : null
                                )}
                            </section>
                        );
                    })}
                </>
            );
        }
        if (tabId === 'pending') {
            // "NOTHING HERE" AND A CARD SITTING UNDERNEATH IT CANNOT BOTH BE TRUE
            // (WI-POST-STUDIO-FINISH-THE-JOB-NO-PARTIAL-EXIT-001 Leg 1b).
            //
            // WHY BOTH USED TO RENDER, established from the code rather than guessed. The empty
            // branch was gated on `drafts.length === 0` ALONE, and WithheldDrafts was returned as
            // its SIBLING immediately underneath. WithheldDrafts withholds itself only when its
            // OWN list is empty (see its early `if (!items.length) return null`). The two
            // conditions were therefore independent, and the one combination nobody checked —
            // no pending drafts BUT withheld drafts present — rendered the "nothing is waiting"
            // sentence directly above a card that was, visibly, waiting.
            //
            // The fix is the condition, not a re-layout: the tab is empty only when it holds
            // NOTHING AT ALL. With a withheld card present the tab has content, so the empty
            // sentence is not shown — the card speaks for itself, which is what it is for.
            const pendingIsEmpty = drafts.length === 0 && withheldDrafts.length === 0;
            if (pendingIsEmpty) {
                return (
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
                        <p className="text-base text-slate-500">{PS_LABELS.pendingEmpty}</p>
                    </div>
                );
            }
            return (
                <div className="space-y-4">
                    {/* THE DECISIONS FIRST, THE ARCHIVE NOTICE AFTER
                        (WI-THE-MAP-IS-THE-TRUTH-IDEA-LANE-001 Task 3, CEO report 2026-08-02.)

                        This block rendered ABOVE the drafts. On the live page today that put a
                        four-line explanation of what archiving means, plus a draft archived on
                        13 July, at the TOP of the tab — and the one draft he actually had to
                        decide on sat underneath it. His words: an archive notice sitting on a
                        decision tab.

                        Pending HOLDS drafts he has not yet decided on (tab_definitions.py).
                        An archived draft is not one of those, so it does not get to open the
                        tab. It is still shown, still named, still reversible — underneath the
                        work. Nothing is hidden and nothing is deleted; the ORDER is the fix,
                        because the order is what said which of the two mattered. */}
                    {drafts.map((post, idx) => (
                        <PendingPostCard
                            key={post._id || post.post_id}
                            post={post}
                            onPosted={handlePosted}
                            onRejected={handleRejected}
                            onSaved={handleSaved}
                            rankPosition={idx + 1}
                            totalInSession={drafts.length}
                            rejectTaxonomy={rejectTaxonomy}
                            announce={announce}
                        />
                    ))}
                    <WithheldDrafts items={withheldDrafts} />
                </div>
            );
        }
        if (tabId === 'saved') {
            if (saved.length === 0) {
                return (
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
                        <p className="text-base text-slate-500">{PS_LABELS.savedEmpty}</p>
                    </div>
                );
            }
            return (
                <div className="space-y-4">
                    {saved.map(post => (
                        <SavedPostCard
                            key={post._id || post.post_id}
                            post={post}
                            onPosted={handlePosted}
                            onRejected={handleRejected}
                            onEdited={() => {}}
                            rejectTaxonomy={rejectTaxonomy}
                            announce={announce}
                        />
                    ))}
                </div>
            );
        }
        if (tabId === 'posted') {
            if (posted.length === 0) {
                return (
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
                        <p className="text-base text-slate-500">{PS_LABELS.postedEmpty}</p>
                    </div>
                );
            }
            return (
                <div className="space-y-4">
                    {posted.map(post => (
                        <HistoryCard
                            key={post._id || post.post_id}
                            post={post}
                            onUnmark={handleUnmark}
                            announce={announce}
                        />
                    ))}
                </div>
            );
        }
        // rejected — THE ONLY TAB THAT CARRIES SUB-TABS
        // (WI-ONE-POST-REACHES-THE-CEO-001 Leg 3, CEO ruling D-2026-07-31-15: "sub-tabs are
        // permitted under REJECTED only — ideas and posts — AND a decided idea leaves the
        // Ideas tab; five tabs otherwise, and no other sub-tabs.")
        //
        // The DESTINATION, and it was built before the departure on purpose: the Ideas-tab
        // exclusion that ships alongside it would otherwise strand four decided cards nowhere
        // at all, which is a worse screen than the one being fixed.
        return (
            <>
                {/* THE SAME TABLIST COMPONENT THE PAGE'S OWN TABS USE, one level down.
                    Reused rather than re-drawn: a hand-rolled second chip row is how the
                    "unknown is not zero" ruling gets dropped, and it was dropped in the
                    first draft of this block — a failed decided-ideas load rendered
                    "Ideas (0)", which is the lie that ruling exists to prevent. Passing
                    `null` for a list that did not load is what makes the badge say so. */}
                <TabChips
                    activeTab={rejectedSubTab}
                    onTabChange={(id) => {
                        setRejectedSubTab(id);
                        // Spoken, not just highlighted — this surface is read by
                        // text-to-speech and a highlight alone is silent.
                        announce((REJECTED_SUBTABS.find(t => t.id === id) || {}).label || id);
                    }}
                    tabCounts={{
                        posts: loadFailed.rejected ? null : rejected.length,
                        ideas: loadFailed.rejectedIdeas ? null : rejectedIdeas.length,
                    }}
                    tabs={REJECTED_SUBTABS}
                    ariaLabel={REJECTED_SUBTAB_ARIA_LABEL}
                    variant="tint"
                />

                {rejectedSubTab === 'ideas' ? (
                    /* THE SAME PANEL THE IDEAS TAB USES, one decision later. A second idea
                       list is exactly the drift IdeaTabPanel was consolidated to end; only
                       the copy differs, and it is passed in rather than forked. Decided
                       cards render without action buttons already — the panel refuses to
                       offer a second decision on a card that carries one. */
                    <IdeaTabPanel
                        ideas={rejectedIdeas}
                        loadFailed={!!loadFailed.rejectedIdeas}
                        statusLine={REJECTED_SUBTAB_LABELS.ideasStatus}
                        emptyText={REJECTED_SUBTAB_LABELS.ideasEmpty}
                    />
                ) : rejected.length === 0 ? (
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
                        <p className="text-base text-slate-500">{PS_LABELS.rejectedEmpty}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {rejected.map(post => (
                            <RejectedPostCard
                                key={post._id || post.post_id}
                                post={post}
                                onOverride={handleOverride}
                                announce={announce}
                                rejectTaxonomy={rejectTaxonomy}
                            />
                        ))}
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            {/* Page Header */}
            <div className="mb-8">
                <h1
                    className="text-[28px] font-extrabold text-brand-900 leading-tight mb-1 font-heading"
                >
                    {MODULE.name}
                </h1>
                <p className="text-base text-slate-500 max-w-2xl leading-relaxed">
                    Review post ideas from the agent.
                </p>
            </div>

            {/* Polite live region — announces action outcomes + tab changes to a
                screen reader (WI-LINKEDIN-SAVE-BUTTON-LIVE-FAILURE-001). Visually
                hidden; the visible toast + tab highlight stay for sighted users. */}
            <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

            {/* Pre-draft CEO idea-card gate (WI-IDEA-CARD-BUILD-001 Leg 4, §2h).
                Announces through the page-level live region above. */}
            <IdeaCardGateToggle announce={announce} />

            {/* Tab chips */}
            <TabChips
                activeTab={activeTab}
                onTabChange={handleTabChange}
                tabCounts={tabCounts}
            />

            {/* Content — a focusable tabpanel so focus lands here after an action
                moves the CEO to a new view; wired to the tabs via PS_TABPANEL_ID. */}
            <div
                ref={contentRef}
                id={PS_TABPANEL_ID}
                role="tabpanel"
                tabIndex={-1}
                aria-label={(PS_TABS.find(t => t.id === activeTab) || {}).label || activeTab}
                className="outline-none"
            >
                {loading ? (
                    <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
                        <SpinnerGap weight="bold" className="w-5 h-5 animate-spin" />
                        <span className="text-base">Loading posts...</span>
                    </div>
                ) : (
                    renderTabContent(activeTab)
                )}
            </div>
        </>
    );
}
